import React from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SendIcon from "@mui/icons-material/Send";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import BarChartIcon from "@mui/icons-material/BarChart";
import QuestionFilterComponent from "./QuestionFilterComponent"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PersonIcon from "@mui/icons-material/Person";
import CakeIcon from "@mui/icons-material/Cake";
import GroupsIcon from "@mui/icons-material/Groups";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import SchoolIcon from "@mui/icons-material/School";
import CategoryIcon from "@mui/icons-material/Category";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TopAreasPanel from "./TopAreasPanel";
import ProviderComparison from "./ProviderComparison";
import TuneIcon from "@mui/icons-material/Tune";
import KeyAreaStats from "./KeyAreaStats"
import {
  Box, Typography, Select, MenuItem, ListSubheader, Stack, IconButton,
  Tooltip, Accordion, AccordionSummary, AccordionDetails, Collapse,
  Table, TableHead, TableBody, TableRow, TableCell, ButtonBase, LinearProgress,
  ToggleButtonGroup, ToggleButton, CircularProgress, TextField,
} from "@mui/material";
import AreaSearch from "./AreaSearch"
import {
  isSegmentFilterAvailable,
  segmentFilterLabel,
  segmentOptionsFor,
} from "./utils/segmentConfig";
import { numericOrNull as numOrNull } from "./utils/keyWeights";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Cell,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ReferenceLine,
} from "recharts";

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  bg: "#ffffff",
  surface: "#f8f9fc",
  surfaceHover: "#f0f2f7",
  border: "rgba(15,23,42,0.07)",
  borderStrong: "rgba(15,23,42,0.13)",
  text: "#0f172a",
  textMuted: "#64748b",
  textFaint: "rgb(128,128,128)",
  accent: "#0f9fab",
  accentDim: "rgba(15,159,171,0.08)",
  accentGlow: "rgba(15,159,171,0.04)",
  activeTab: "#e8fbfb",
  chip: "#f3f4f6",
  chipActive: "rgba(15,159,171,0.1)",
  chipActiveBorder: "rgba(15,159,171,0.3)",
};

// ─── Shared sub-components ────────────────────────────────────────────────────
const FilterChip = ({ label, active, onClick }) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      px: 1.25,
      py: 0.5,
      borderRadius: "6px",
      border: "1px solid",
      borderColor: active ? T.chipActiveBorder : T.border,
      bgcolor: active ? T.chipActive : T.chip,
      color: active ? T.accent : T.textMuted,
      fontSize: "11px",
      fontWeight: active ? 600 : 400,
      fontFamily: "'DM Sans', sans-serif",
      letterSpacing: "0.02em",
      transition: "all 0.15s ease",
      "&:hover": {
        borderColor: active ? T.chipActiveBorder : T.borderStrong,
        bgcolor: active ? T.chipActive : T.surfaceHover,
        color: active ? T.accent : T.text,
      },
    }}
  >
    {label}
  </ButtonBase>
);

const DemographicGroup = ({ icon: Icon, label, iconColor, children }) => (
  <Box>
    <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.75 }}>
      <Icon sx={{ fontSize: 13, color: iconColor || T.textFaint }} />
      <Typography sx={{ fontSize: "10px", fontWeight: 600, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif" }}>
        {label}
      </Typography>
    </Stack>
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {children}
    </Stack>
  </Box>
);

const StyledAccordion = ({ children, defaultExpanded, ...props }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    elevation={0}
    disableGutters
    {...props}
    sx={{
      bgcolor: "transparent",
      border: "none",
      borderBottom: `1px solid ${T.border}`,
      borderRadius: "0 !important",
      "&:before": { display: "none" },
      "&.Mui-expanded": { margin: 0 },
      ...props.sx,
    }}
  >
    {children}
  </Accordion>
);

const StyledAccordionSummary = ({ children, ...props }) => (
  <AccordionSummary
    expandIcon={<ExpandMoreIcon sx={{ fontSize: 16, color: T.textFaint }} />}
    {...props}
    sx={{
      px: 2.5,
      py: 0,
      minHeight: "44px !important",
      "&.Mui-expanded": { minHeight: "44px !important" },
      "& .MuiAccordionSummary-content": { my: "0 !important", alignItems: "center" },
      "&:hover": { bgcolor: T.accentGlow },
      transition: "background 0.15s ease",
    }}
  >
    {children}
  </AccordionSummary>
);

const SectionLabel = ({ children }) => (
  <Typography sx={{
    fontSize: "11px",
    fontWeight: 700,
    color: T.textFaint,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    fontFamily: "'DM Sans', sans-serif",
  }}>
    {children}
  </Typography>
);

const Sidebar = ({
  isPanelOpen = true,
  togglePanel = () => {},
  sectionTitle = "Explore",
  blurbText = "",
  selectedAreaLabel,
  groupsById,
  selectedVariable,
  onVariableChange,
  genderFilter,        
  onGenderChange,
  ageFilter,          
  onAgeChange,
  ethnicityFilter,     
  onEthnicityChange,
  partyFilter,     
  onPartyChange,
  educationFilter,
  onEducationChange,
  segmentFilter,
  onSegmentChange,
  preferenceFilter,
  onPreferenceFilterChange,
  Datablurb,
  selectedFeatureData, 
  onClearAll,
  sidebarData,
  theme_colours,
  selectedconstName,
  selectedFeatureLevel,
  ActionTargetVariables,
  onActionTargetVariableChange,
  ActionTargetSubVariables,
  handleActionTargetSubVariableChange,
  ActionTargetRanges,
  ActionTargetMAXRanges,
  handleActionTargetRangesChange,
  ActionTargetConcentration,
  handleActionTargetConcentrationChange,
  audienceInsight,
  audienceInsightLoading,
  onAudienceInsightRequest,
  ActionMode,
  handleActionModeChange,
  selectedDemographics,
  rankedItems,
  mapLoading,
  onAreaSelect,
  hoverArea,
  poliInfo,
  geoLevel,
  selectedAreaId,
  selectedRowMatchIds,
  selectedFeature,
  poliInfoToplevel,
  extDataBlurb,
  extDataBlurbComparison,
  colorLabels,
  user,
  dynamicSlug,
  providerFilter,
  providerFilterOriginalVar,
  onProviderFilterChange,
  waveSelect,
  compareWaves,
  selectedconstCode,
  showChatTab,
  chatHighlightCount,
  onClearHighlights,
  activeTab = "data",
  onActiveTabChange = () => {},
  chatMessages = [],
  chatInput = "",
  onChatInputChange = () => {},
  chatLoading = false,
  onChatSend = () => {},
  onChatFlag = () => {},
  chatQueryOpen = null,
  onChatQueryOpenChange = () => {},
}) => {
  const isSel = (val, target) => val === target;
  const toggleVal = (curr, next) => (curr === next ? null : next);
  // Segment supports multi-select on tracking; the energy schema's electricity
  // provider is single-select — picking a provider replaces any previous pick.
  const isSegSel = (arr, val) => Array.isArray(arr) ? arr.includes(val) : arr === val;
  const toggleSegVal = (curr, next) => {
    const arr = Array.isArray(curr) ? curr : curr != null ? [curr] : [];
    if (groupsById?.[selectedVariable]?.schema === "energy") {
      return arr.includes(next) ? [] : [next];
    }
    return arr.includes(next) ? arr.filter((v) => v !== next) : [...arr, next];
  };
  const [detailsOpen] = React.useState(true);

  const chatBottomRef = React.useRef(null);

  React.useEffect(() => {
    if (activeTab === 'chat' && chatBottomRef.current) {
      chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [chatMessages, activeTab]);
  const [compareChartMode, setCompareChartMode] = React.useState("bars");
  const [vsAvgMode, setVsAvgMode] = React.useState(false);
  const [activeWaveKeys, setActiveWaveKeys] = React.useState(null); // null = all active
  const [trendAnimate, setTrendAnimate] = React.useState(true);
  const [flagOpen, setFlagOpen] = React.useState(null); // index of message with flag UI open
  const [flagCategory, setFlagCategory] = React.useState("bad_answer");
  const [flagNote, setFlagNote] = React.useState("");
  const [flagSubmitted, setFlagSubmitted] = React.useState({}); // { [msgIndex]: true }
  const [hoveredTrendKey, setHoveredTrendKey] = React.useState(null);
  const [showNationalLine, setShowNationalLine] = React.useState(false);

  React.useEffect(() => {
    if (compareWaves) {
      setCompareChartMode("trend");
    } else {
      setCompareChartMode("bars");
    }
  }, [compareWaves, selectedAreaLabel]);

  React.useEffect(() => {
    setActiveWaveKeys(null); // reset toggled parties when question changes
    setVsAvgMode(false);    // reset vs-avg mode when question changes
  }, [selectedVariable]);


  const trendAnimSignature = React.useMemo(() => {
    const ws = Array.isArray(selectedFeatureData?.waveSeries) ? selectedFeatureData.waveSeries : [];
    const firstWave = ws[0]?.wave ?? "";
    const lastWave = ws[ws.length - 1]?.wave ?? "";
    return [
      selectedVariable || "",
      selectedAreaLabel || "",
      compareChartMode,
      ws.length,
      firstWave,
      lastWave,
      activeWaveKeys ? Array.from(activeWaveKeys).sort().join(",") : "__all__",
    ].join("|");
  }, [selectedVariable, selectedAreaLabel, compareChartMode, selectedFeatureData?.waveSeries, activeWaveKeys]);

  React.useEffect(() => {
    setTrendAnimate(true);
    const t = setTimeout(() => setTrendAnimate(false), 450);
    return () => clearTimeout(t);
  }, [trendAnimSignature]);

  const selected = selectedVariable ? groupsById?.[selectedVariable] : null;

  // The segment filter is schema-specific: the tracking schema breaks down by
  // attitudinal segment, the energy schema by electricity provider. Column,
  // heading and codes all come from segmentConfig so the two chip groups below
  // (explore + client) stay in step.
  const segmentAvailable = isSegmentFilterAvailable(selected, {
    isClientSection: sectionTitle === "Client",
  });
  const segmentGroupLabel = segmentFilterLabel(selected?.schema);
  const segmentOptions = segmentOptionsFor(selected?.schema);

  const targetGroup = groupsById?.[ActionTargetVariables?.TargetVariable1];
  const targetKey = ActionTargetSubVariables?.TargetSubVariable1;
  const targetLabel = (() => {
    if (!targetKey) return "Select a response";
    if (targetKey === "net") return "Net";
    if (targetKey === "top") return "Top ranked";
    if (targetKey === "compare") return "Compare all";
    // likert_grid stores preferenceFilter as "subVarId|responseKey"
    if (targetGroup?.type === "likert_grid" && targetKey.includes("|")) {
      const [subVarId, responseKey] = targetKey.split("|");
      const subLabel = targetGroup?.subLabels?.[subVarId] || subVarId;
      const subVarDef = groupsById?.[subVarId];
      const TOKEN_LABELS = { net: "Net", trust: "Trust", distrust: "Distrust", comfort: "Comfort", discomfort: "Discomfort" };
      const responseLabel = TOKEN_LABELS[String(responseKey).toLowerCase()]
        || subVarDef?.labels?.[responseKey] || subVarDef?.full_labels?.[responseKey] || responseKey;
      return `${subLabel} — ${responseLabel}`;
    }
    return targetGroup?.full_labels?.[targetKey] || targetGroup?.labels?.[targetKey] || targetKey;
  })();
  const targetGroupLabel = targetGroup?.label || "Response";
 // --- Dynamic text width measurement ---
// const measureTextWidth = React.useMemo(() => {
//   const canvas = document.createElement("canvas");
//   const ctx = canvas.getContext("2d");
//   return (text, font = "12px Inter, Roboto, Arial, sans-serif") => {
//     if (!ctx) return 120;
//     ctx.font = font;
//     return ctx.measureText(String(text ?? "")).width;
//   };
// }, []);

// const [yAxisWidth, setYAxisWidth] = React.useState(120);
// React.useEffect(() => {
//   if (!window.__lastCombinedForYAxis) return;
//   const combined = window.__lastCombinedForYAxis;
//   const pad = 10, minW = 80, maxW = 180;
//   const longest = combined.reduce(
//     (m, d) => Math.max(m, measureTextWidth(d.label)),
//     0
//   );
//   setYAxisWidth(Math.min(maxW, Math.max(minW, Math.ceil(longest) + pad)));
// }, []);

// const TickWrap = React.useCallback(
//   ({ x, y, payload }) => {
//     const text = String(payload?.value ?? "");
//     const maxLineWidth = Math.min(yAxisWidth - 12, 160);
//     const words = text.split(" ");
//     const lines = [];
//     let line = [];
//     const lineWidth = (arr) => measureTextWidth(arr.join(" "));
//     words.forEach((w) => {
//       const test = [...line, w];
//       if (lineWidth(test) > maxLineWidth && line.length) {
//         lines.push(line.join(" "));
//         line = [w];
//       } else line = test;
//     });
//     if (line.length) lines.push(line.join(" "));
//     return (
//       <text
//         x={x - 5}
//         y={y}
//         textAnchor="end"
//         fontSize={12}
//         fill="#333"
//         style={{ pointerEvents: "none" }}
//       >
//         {lines.map((t, i) => (
//           <tspan key={i} x={x - 5} dy={i === 0 ? 0 : 12}>
//             {t}
//           </tspan>
//         ))}
//       </text>
//     );
//   },
//   [measureTextWidth, yAxisWidth]
// );

React.useEffect(() => {
  console.log("🧭 Sidebar feature level change →", selectedFeatureLevel);
  console.log("selectedconstName →", selectedconstName);
  console.log("selectedAreaLabel →", selectedAreaLabel);
}, [selectedFeatureLevel, selectedconstName, selectedAreaLabel]);

const variableKeys = Object.keys(groupsById || {}).filter(
  (v) => v !== "turnout" && !groupsById[v]?.hidden
);
const isDemoUser = Boolean(user?.is_demo_user);
const DEMO_LOCKED_LABELS = [
  "sector trust",
  "trust drivers",
  "local business challenges",
];
const isDemoLockedVariable = (variableKey) => {
  if (!isDemoUser) return false;
  const label = String(groupsById?.[variableKey]?.label || variableKey || "").toLowerCase();
  return DEMO_LOCKED_LABELS.some((locked) => label.includes(locked));
};
const selectableVariableKeys = variableKeys.filter((v) => !isDemoLockedVariable(v));
const defaultVariable = (isDemoUser ? selectableVariableKeys[0] : variableKeys[0]) || "";

const CATEGORY_ORDER = ["political", "business", "motability", "waspi","mccain"];
const CATEGORY_LABELS = { political: "Political", business: "Business & Industry", motability: "Motability", waspi: "WASPI",mccain:"McCain" };
const HOLIDAY_TAX_VARIABLES = new Set([
  "holiday_tax_vote",
  "holiday_tax_support",
  "decline_pride",
  "tourist_tax_concerns",
  "tourist_tax_impact_travel",
]);
function ClientLogo({ slug, style }) {
  const [extIdx, setExtIdx] = React.useState(0);
  const exts = ["svg", "png"];
  if (extIdx >= exts.length) return null;
  return (
    <img
      src={`${process.env.PUBLIC_URL}/logos/${slug}.${exts[extIdx]}`}
      alt="Client logo"
      style={style}
      onError={() => setExtIdx((i) => i + 1)}
    />
  );
}

const LOGO_SLUG_ALIASES = {
  "butlins": "butlin's",
  "butlin-s": "butlin's",
  "haven-holidays": "haven",
  "hilton-hotels": "hilton",
  "uk-hospitality": "ukhospitality",
  "stackdatastrategy": "stack-data-strategy",
  "octopus": "octoenergy",
  "waspi": "waspi",
  "mccain":"mccain"
};
const DISPLAY_COMPANY_ALIASES = {
  "butlins": "Butlin's",
};
const logoSlug = LOGO_SLUG_ALIASES[String(dynamicSlug || "").toLowerCase()] || dynamicSlug;
const displayCompanyName =
  DISPLAY_COMPANY_ALIASES[String(user?.company || "").trim().toLowerCase()] ||
  user?.company;
const demoVariant = String(user?.demo_variant || "default").trim().toLowerCase();
const isHolidayTaxDemo = demoVariant === "holiday-tax-demo";
const [showClientLogo, setShowClientLogo] = React.useState(true);
const [logoAttemptIdx, setLogoAttemptIdx] = React.useState(0);
const logoCandidates = React.useMemo(() => {
  const slugs = [
    String(logoSlug || "").toLowerCase(),
    String(dynamicSlug || "").toLowerCase(),
  ].filter(Boolean);
  const uniq = Array.from(new Set(slugs));
  return uniq.flatMap((s) => [
    `${process.env.PUBLIC_URL}/logos/${s}.svg`,
    `${process.env.PUBLIC_URL}/logos/${s}.png`,
  ]);
}, [dynamicSlug, logoSlug]);
const logoSrc = logoCandidates[logoAttemptIdx] || "";

React.useEffect(() => {
  setShowClientLogo(true);
  setLogoAttemptIdx(0);
}, [dynamicSlug, logoSlug]);
const groupedVariables = CATEGORY_ORDER.reduce((acc, cat) => {
  acc[cat] = variableKeys.filter((v) => groupsById[v]?.category === cat);
  return acc;
}, {});
const uncategorizedVariables = variableKeys.filter((v) => !groupsById[v]?.category);

if (selectedVariable && !variableKeys.includes(selectedVariable) && !providerFilter) {
  // Force the change immediately (but not when a provider filter swapped the variable)
  onVariableChange?.(defaultVariable);
}
if (isDemoUser && selectedVariable && isDemoLockedVariable(selectedVariable)) {
  onVariableChange?.(defaultVariable);
}

// ── Nation of the selected area, from the ONS code prefix ───────────────────
// Mirrors the SNP/Plaid suppression already applied to vote-intention response
// options further down, but at component scope so the demographic panels can
// use it too: filtering by SNP or Plaid past vote in an English seat targets a
// group that cannot exist there. With no area selected (national scope) every
// option stays, because Scottish and Welsh seats are in scope.
const _pastVoteAreaCodeRaw =
  selectedFeature?.value || selectedFeature?.code || selectedFeature?.id ||
  selectedFeature?.rowMatchIds?.[0] || selectedAreaId ||
  selectedFeature?.properties?.PCON24CD || selectedFeature?.properties?.WD24CD ||
  selectedFeature?.properties?.LAD23CD || null;
const _pastVoteAreaCode =
  _pastVoteAreaCodeRaw == null ? "" : String(_pastVoteAreaCodeRaw).trim().toUpperCase();
const areaNationKnown = _pastVoteAreaCode.length > 0;
const areaIsScotland = areaNationKnown && _pastVoteAreaCode.startsWith("S");
const areaIsWales    = areaNationKnown && _pastVoteAreaCode.startsWith("W");

const PARTY_SNP = 6, PARTY_PLAID = 7;
// Shared by the explore-mode and target-mode demographic panels so the two
// can't drift apart.
const pastVoteOptions = [
  [1, "Conservative"], [2, "Labour"], [3, "Reform"], [4, "Lib Dem"],
  [5, "Green"], [PARTY_SNP, "SNP"], [PARTY_PLAID, "Plaid Cymru"],
  [8, "Independent/Other"], [9, "Non-voter"],
].filter(([val]) => {
  if (!areaNationKnown) return true;
  if (val === PARTY_SNP) return areaIsScotland;
  if (val === PARTY_PLAID) return areaIsWales;
  return true;
});

// Drop an active past-vote filter that is no longer offered here — otherwise
// drilling from a Scottish seat into an English one leaves an SNP filter live
// with no chip to clear it, and every figure silently reads off ~0 respondents.
const pastVoteSelectable = pastVoteOptions.some(([val]) => val === partyFilter);
React.useEffect(() => {
  if (partyFilter != null && !pastVoteSelectable) onPartyChange?.(null);
}, [partyFilter, pastVoteSelectable, onPartyChange]);


  return (
    


<Box sx={{ position: "relative", height: "100%", display: "flex", flexDirection: "row", zIndex: 1200 }}>

  <Box
  id="sidebar-v2"
  sx={{
    width: isPanelOpen ? `100%` : "0px",
    height: "calc(100vh - 64px)",
    bgcolor: T.bg,
    boxShadow: isPanelOpen ? "4px 0 20px rgba(0,0,0,0.08)" : "none",
    transition: "width 320ms cubic-bezier(0.22, 1, 0.36, 1)",
    opacity: isPanelOpen ? 1 : 0,
    pointerEvents: isPanelOpen ? "auto" : "none",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    fontFamily: "'DM Sans', sans-serif",
    "@import": "url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap')",
  }}
>
<Box
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 10,
        bgcolor: T.bg,
        borderBottom: `1px solid ${T.border}`,
      }}
    >
    
      <Box
        sx={{
          py: 1.5,
          px: 3, 
          // borderBottom: `1px solid ${T.border}`,
        }}
      >
        {sectionTitle === "Client" ? (
          <Box sx={{ textAlign: "left" }}>
            {!isHolidayTaxDemo && showClientLogo && (
              <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
                <img
                  src={logoSrc}
                  alt="Constituency Profiles"
                  onError={() => {
                    if (logoAttemptIdx < logoCandidates.length - 1) {
                      setLogoAttemptIdx((i) => i + 1);
                    } else {
                      setShowClientLogo(false);
                    }
                  }}
                  style={{ height: 40, objectFit: "contain" }}
                />
              </Box>
            )}

            <Typography
              variant="h6"
              align="center" 
              sx={{ fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif", fontSize: "15px", letterSpacing: "-0.01em" }}
            >
              {isHolidayTaxDemo ? "Holiday Tax" : displayCompanyName}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif", fontSize: "15px", letterSpacing: "-0.01em" }} 
          >
            Tracking Data
          </Typography>
          </Box>
        )}
      </Box>

      <Box sx={{ display: "flex", px: 2.5, pb: 1.5, gap: 0.75 }}>
        {/* Explore Variable — primary */}
        {(() => {
          const isActive = ActionMode === "explore";
          return (
            <ButtonBase
              disableRipple
              onClick={() => handleActionModeChange("explore")}
              sx={{
                flex: 1, py: 0.85, px: 1.5, borderRadius: "8px",
                border: `1px solid ${isActive ? T.accent : T.border}`,
                bgcolor: isActive ? T.accent : "#ffffff",
                color: isActive ? "#ffffff" : T.textMuted,
                fontSize: "12px", fontWeight: 600,
                fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em",
                transition: "all 0.18s ease",
                display: "flex", alignItems: "center", justifyContent: "center",
                boxShadow: isActive ? "0 2px 8px rgba(15,159,171,0.25)" : "none",
                "&:hover": { bgcolor: isActive ? "#0d8f9a" : T.activeTab, borderColor: T.accent, color: isActive ? "#ffffff" : T.accent },
                "&:focus": { outline: "none" },
              }}
            >
              Explore Variable
            </ButtonBase>
          );
        })()}
        {/* Find Target Areas — secondary */}
        {(() => {
          const isActive = ActionMode === "filter";
          return (
            <ButtonBase
              disableRipple
              onClick={() => handleActionModeChange("filter")}
              sx={{
                flex: 1, py: 0.85, px: 1.5, borderRadius: "8px",
                border: `1px solid ${isActive ? T.chipActiveBorder : T.border}`,
                bgcolor: isActive ? T.activeTab : "transparent",
                color: isActive ? T.accent : T.textMuted,
                fontSize: "12px", fontWeight: isActive ? 600 : 400,
                fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em",
                transition: "all 0.18s ease",
                display: "flex", alignItems: "center", justifyContent: "center",
                "&:hover": { borderColor: T.chipActiveBorder, bgcolor: T.activeTab, color: T.accent },
                "&:focus": { outline: "none" },
              }}
            >
              Find Target Areas
            </ButtonBase>
          );
        })()}
      </Box>

      {/* ── Top-level tabs: Data / Ask AI — segmented control ── */}
      {showChatTab && (
        <Box sx={{ px: 2.5, pb: 1, pt: 0.25 }}>
          <Box sx={{
            display: "flex", p: "3px", borderRadius: "10px",
            bgcolor: "rgba(15,23,42,0.05)", border: `1px solid ${T.border}`,
          }}>
            {[
              { key: "data", label: "Data", Icon: BarChartIcon },
              { key: "chat", label: "Ask AI", Icon: SmartToyOutlinedIcon },
            ].map(({ key, label, Icon }) => {
              const isActive = activeTab === key;
              return (
                <ButtonBase
                  key={key}
                  onClick={() => onActiveTabChange(key)}
                  sx={{
                    flex: 1, py: 0.65, px: 1.5, borderRadius: "7px",
                    bgcolor: isActive ? "#ffffff" : "transparent",
                    boxShadow: isActive ? "0 1px 4px rgba(15,23,42,0.1)" : "none",
                    color: isActive ? T.accent : T.textMuted,
                    fontSize: "12px", fontWeight: isActive ? 700 : 500,
                    fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.01em",
                    transition: "all 0.18s ease",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5,
                    "&:hover": { color: isActive ? T.accent : T.text },
                    "&:focus": { outline: "none" },
                  }}
                >
                  <Icon sx={{ fontSize: 13 }} />
                  {label}
                </ButtonBase>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>

    {activeTab === "data" && ActionMode === "explore" &&(
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": { width: "4px" },
        "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
        "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
      }}
    >
      {/* Variable Selector */}
      <Box id="tour-variable-selector" sx={{ p: 2.5, borderBottom: `1px solid ${T.border}` }}>
        <SectionLabel>Data Variable</SectionLabel>
        <Box sx={{ mt: 1.25 }}>
          <Select
            value={selectedVariable ?? defaultVariable}
            onChange={(e) => onVariableChange?.(e.target.value)}
            displayEmpty
            size="small"
            fullWidth
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: T.surface,
                  border: `1px solid ${T.borderStrong}`,
                  borderRadius: "10px",
                  mt: 0.5,
                  boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    color: T.textMuted,
                    fontFamily: "'DM Sans', sans-serif",
                    py: 0.75,
                    px: 2,
                    borderRadius: "6px",
                    mx: 0.5,
                    "&:hover": { bgcolor: T.surfaceHover, color: T.text },
                    "&.Mui-selected": { bgcolor: T.accentDim, color: T.accent, fontWeight: 600 },
                  },
                },
              },
            }}
            sx={{
              borderRadius: "8px",
              bgcolor: T.chip,
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: T.border,
                transition: "border-color 0.15s",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: T.borderStrong },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: T.accent },
              "& .MuiSelect-select": {
                color: T.text,
                fontSize: "13px",
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 500,
                textAlign: "left !important",
                display: "flex !important",
                alignItems: "center !important",
                py: 1.1,
              },
              "& .MuiSvgIcon-root": { color: T.textFaint },
            }}
          >
            {CATEGORY_ORDER.flatMap((cat) => {
              const vars = groupedVariables[cat];
              if (!vars?.length) return [];
              const regularVars = cat === "business"
                ? vars.filter((v) => !HOLIDAY_TAX_VARIABLES.has(v))
                : vars;
              const holidayVars = cat === "business"
                ? vars.filter((v) => HOLIDAY_TAX_VARIABLES.has(v))
                : [];
              const showCategoryHeader = cat !== "business" || regularVars.length > 0;
              return [
                ...(showCategoryHeader
                  ? [(
                      <ListSubheader
                        key={`subheader-${cat}`}
                        disableSticky
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          fontFamily: "'DM Sans', sans-serif",
                          letterSpacing: "0.07em",
                          textTransform: "uppercase",
                          color: T.textFaint,
                          lineHeight: "28px",
                          bgcolor: T.surface,
                          px: 2,
                        }}
                      >
                        {CATEGORY_LABELS[cat]}
                      </ListSubheader>
                    )]
                  : []),
                ...regularVars.map((variable) => (
                  <MenuItem key={variable} value={variable} disabled={isDemoLockedVariable(variable)}>
                      <span>{groupsById[variable]?.label || variable}
                      {isDemoLockedVariable(variable) && (
                        <LockOutlinedIcon sx={{ fontSize: 14, ml: .5, color: T.textFaint }} />
                     )}
                 </span>
                  </MenuItem>
                )),
                ...(holidayVars.length
                  ? [
                      <ListSubheader
                        key="subheader-business-holiday-tax"
                        disableSticky
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          fontFamily: "'DM Sans', sans-serif",
                          letterSpacing: "0.07em",
                          textTransform: "uppercase",
                          color: T.textFaint,
                          lineHeight: "24px",
                          bgcolor: T.surface,
                          px: 2,
                        }}
                      >
                        Holiday tax
                      </ListSubheader>,
                      ...holidayVars.map((variable) => (
                        <MenuItem key={variable} value={variable} disabled={isDemoLockedVariable(variable)}>
                            <span>{groupsById[variable]?.label || variable}
                            {isDemoLockedVariable(variable) && (
                              <LockOutlinedIcon sx={{ fontSize: 14, ml: .5, color: T.textFaint }} />
                            )}
                       </span>
                        </MenuItem>
                      )),
                    ]
                  : []),
              ];
            })}
            {uncategorizedVariables.map((variable) => (
              <MenuItem key={variable} value={variable} disabled={isDemoLockedVariable(variable)}>
                <Box sx={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                  <span>{groupsById[variable]?.label || variable}</span>
                  {isDemoLockedVariable(variable) && (
                    <LockOutlinedIcon sx={{ fontSize: 14, color: T.textFaint }} />
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
        

        {/* Question details row */}
        {selected?.fulltext && (
          <>
            <Box sx={{ mt: 1.5 }}>
              <Stack direction="row" alignItems="flex-start" spacing={0.75}>
                <InfoOutlinedIcon sx={{ fontSize: 13, color: T.textFaint, mt: "2px", flexShrink: 0 }} />
                <Collapse in={detailsOpen} timeout="auto" unmountOnExit>
                  <Typography sx={{ fontSize: "12px", color: T.textFaint, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
                    {selected.fulltext}
                  </Typography>
                </Collapse>
              </Stack>
            </Box>
          </>
        )}
        {/* Provider filter badge */}
        {providerFilter && providerFilterOriginalVar && (() => {
          const origConfig = groupsById?.[providerFilterOriginalVar]?.providerFilter;
          const activeProvider = origConfig?.providers?.find((p) => p.key === providerFilter);
          if (!activeProvider) return null;
          return (
            <Box sx={{ mt: 1 }}>
              <Typography
                component="span"
                sx={{
                  display: "inline-block",
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "#0d9488",
                  bgcolor: "rgba(13, 148, 136, 0.08)",
                  border: "1px solid rgba(13, 148, 136, 0.25)",
                  borderRadius: "12px",
                  px: 1.5,
                  py: 0.25,
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Filtered: {activeProvider.label} customers
              </Typography>
            </Box>
          );
        })()}

      {/* Question Filter */}
      <Box sx={{ mt: 1.5 }}>
        <QuestionFilterComponent
          groupsById={groupsById}
          selectedVariable={selectedVariable}
          onVariableChange={onVariableChange}
          preferenceFilter={preferenceFilter}
          onPreferenceFilterChange={onPreferenceFilterChange}
          ActionMode={ActionMode}
          selectedFeatureData={selectedFeatureData}
          keysWithData={selectedFeatureData?.keysWithData}
          providerFilter={providerFilter}
          providerFilterOriginalVar={providerFilterOriginalVar}
          onProviderFilterChange={onProviderFilterChange}
          clientSlug={dynamicSlug}
        />
      </Box>


      </Box>

{(() => {
  const g = groupsById?.[selectedVariable];
  if (!g) return null;
  return (
    <Box sx={{ px: 2.5, pb: 0.5 }}>
      <ProviderComparison
        group={g}
        schema={g.schema}
        waveSelect={waveSelect}
        segmentFilter={segmentFilter}
        onSegmentChange={onSegmentChange}
        clientSlug={dynamicSlug}
        areaCode={selectedFeatureLevel === "ward" ? (selectedconstCode || null) : (selectedFeature?.value || null)}
        areaLevel={selectedFeatureLevel || "constituency"}
        wardCode={selectedFeatureLevel === "ward" ? (selectedFeature?.value || null) : null}
        selectedKey={preferenceFilter}
        demographics={{ gender: genderFilter, age: ageFilter, ethnicity: ethnicityFilter, party: partyFilter, education: educationFilter }}
        onDemographicChange={(key, value) => {
          const handlers = {
            gender: onGenderChange, age: onAgeChange, ethnicity: onEthnicityChange,
            party: onPartyChange, education: onEducationChange,
          };
          handlers[key]?.(value);
        }}
        areaLabel={selectedFeatureLevel === "ward" ? (selectedAreaLabel || "Ward") : (selectedconstName || null)}
      />
    </Box>
  );
})()}

      {!groupsById?.[selectedVariable]?.hideDemographicFilters && (
      <StyledAccordion id="tour-demographics">
        <StyledAccordionSummary>
          <Stack direction="row" spacing={1} alignItems="center">
            <TuneIcon sx={{ fontSize: 14, color: T.textFaint }} />
            <SectionLabel>Filter by Demographics</SectionLabel>
          </Stack>
        </StyledAccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
          <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
            Explore how different groups respond.
          </Typography>

          <Stack spacing={1.75}>
            <Stack direction="row" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                  {[[2, "Men"], [1, "Women"]].map(([val, lbl]) => (
                    <FilterChip key={val} label={lbl} active={isSel(genderFilter, val)}
                      onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                  ))}
                </DemographicGroup>
              </Box>
              <Box sx={{ flex: 1.5 }}>
                <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                  {[[1, "18–34"], [2, "35–64"], [3, "65+"]].map(([val, lbl]) => (
                    <FilterChip key={val} label={lbl} active={isSel(ageFilter, val)}
                      onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                  ))}
                </DemographicGroup>
              </Box>
            </Stack>

            <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
              {[[1, "Degree"], [2, "No Degree"]].map(([val, lbl]) => (
                <FilterChip key={val} label={lbl} active={isSel(educationFilter, val)}
                  onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
              ))}
            </DemographicGroup>

            <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
              {[[1, "White"], [2, "Non White"]].map(([val, lbl]) => (
                <FilterChip key={val} label={lbl} active={isSel(ethnicityFilter, val)}
                  onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))} />
              ))}
            </DemographicGroup>

            <DemographicGroup icon={HowToVoteIcon} label="Past Vote (2024)" iconColor="#f87171">
              {pastVoteOptions.map(([val, lbl]) => (
                <FilterChip key={val} label={lbl} active={isSel(partyFilter, val)}
                  onClick={() => onPartyChange?.(toggleVal(partyFilter, val))} />
              ))}
            </DemographicGroup>

            {segmentAvailable && (
              <DemographicGroup icon={CategoryIcon} label={segmentGroupLabel} iconColor="#a855f7">
                {segmentOptions.map(([val, lbl]) => (
                  <FilterChip key={val} label={lbl} active={isSegSel(segmentFilter, val)}
                    onClick={() => onSegmentChange?.(toggleSegVal(segmentFilter, val))} />
                ))}
              </DemographicGroup>
            )}
          </Stack>

          {onClearAll && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <ButtonBase
                onClick={onClearAll}
                sx={{
                  fontSize: "11px",
                  color: T.textFaint,
                  fontFamily: "'DM Sans', sans-serif",
                  fontWeight: 500,
                  letterSpacing: "0.03em",
                  px: 1.25,
                  py: 0.5,
                  borderRadius: "6px",
                  border: `1px solid ${T.border}`,
                  transition: "all 0.15s",
                  "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                }}
              >
                Clear all
              </ButtonBase>
            </Box>
          )}
        </AccordionDetails>
      </StyledAccordion>
      )}
{(() => {
  const group = groupsById?.[selectedVariable];
  if (!group) return null;

  // For likert_grid: show sector net trust in compare-all mode;
  // in sector mode, use the sub-group as the effective chart group.
  let chartGroup = group;
  let sectorNetCombined = null; // filled below for likert_grid compare mode

  if (group.type === "likert_grid") {
    const validSubVarIds = group.subVariables || [];
    const isValidSubVar = validSubVarIds.includes((preferenceFilter || "").split("|")[0]);
    const isCompare = !preferenceFilter || preferenceFilter === "compare" || !isValidSubVar;

    if (isCompare) {
      // Build a {key: value} map from raw selectedFeatureData arrays
      const toKeyMap = (arr) => {
        const m = {};
        (arr || []).forEach(({ key, value }) => { if (key) m[key] = Number(value); });
        return m;
      };
      const nationalMap    = toKeyMap(selectedFeatureData?.national);
      const constituencyMap = toKeyMap(selectedFeatureData?.constituency);
      const selectedMap    = toKeyMap(selectedFeatureData?.selected);
      // Unfiltered ("Everyone") series — required by the demographic compare
      // mode; without these fields the Everyone column renders as dashes as
      // soon as a demographic filter is active.
      const unfNationalMap     = toKeyMap(selectedFeatureData?.unfilteredNational);
      const unfConstituencyMap = toKeyMap(selectedFeatureData?.unfilteredConstituency);
      const unfSelectedMap     = toKeyMap(selectedFeatureData?.unfilteredSelected);

      const netFromMap = (byKey, polarity) => {
        if (!polarity) return null;
        const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
        const posKeys = toArr(polarity.pos);
        const negKeys = toArr(polarity.neg);
        const hasData = [...posKeys, ...negKeys].some(k => Number.isFinite(byKey[k]));
        if (!hasData) return null;
        const pos = posKeys.reduce((s, k) => s + (Number.isFinite(byKey[k]) ? byKey[k] : 0), 0);
        const neg = negKeys.reduce((s, k) => s + (Number.isFinite(byKey[k]) ? byKey[k] : 0), 0);
        return pos - neg;
      };

      sectorNetCombined = validSubVarIds.map(svId => {
        const subGroup = groupsById?.[svId];
        // getAggregatesForSidebar's likert_grid override delivers per-sector
        // nets keyed by sub-variable id; older payloads carry raw response
        // keys. Accept both, otherwise every sector reads null and the whole
        // Compare by Geographies section hides itself.
        const valueFrom = (map) =>
          Number.isFinite(map[svId]) ? map[svId] : netFromMap(map, subGroup?.polarity);
        return {
          label: group.subLabels?.[svId] || svId,
          color: group.subColors?.[svId] || "#6e6e6e",
          national:     valueFrom(nationalMap),
          constituency: valueFrom(constituencyMap),
          selected:     valueFrom(selectedMap),
          unfilteredNational:     valueFrom(unfNationalMap),
          unfilteredConstituency: valueFrom(unfConstituencyMap),
          unfilteredSelected:     valueFrom(unfSelectedMap),
        };
      });
    } else {
      const [subVarId] = preferenceFilter.split("|");
      chartGroup = groupsById?.[subVarId] || group;
    }
  }

  // color + label helpers
  const baseColorForLikertKey = (g, key) => {
    const pol = g?.polarity || {};
    const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
    if (toArrPol(pol.pos).includes(key)) return "#2E7D32";
    if (toArrPol(pol.neg).includes(key)) return "#C62828";
    return "#6e6e6e";
  };
  const buildLabelsAndColors = (groupsById, theme_colours) => {
    const labels = {}, colors = {};
    Object.values(groupsById || {}).forEach((g) => {
      if (g.type === "likert_grid") return;
      let i = 0;
      (g.keys || []).forEach((k) => {
        labels[k] = g.full_labels?.[k] || g.labels?.[k] || k;
        if (g.colors?.[k]) colors[k] = g.colors[k];
        else if (g.type === "likert") colors[k] = baseColorForLikertKey(g, k);
        else {
          colors[k] = theme_colours[i % theme_colours.length];
          i += 1;
        }
      });
    });
    return { labels, colors };
  };

  const fmtVal = (v) =>
    v == null || !Number.isFinite(v)
      ? "—"
      : group?.type === "price_elast"
        ? `$${(v / 100).toFixed(2)}`
        : `${v.toFixed(1)}%`;

  const { labels, colors } = buildLabelsAndColors(groupsById, theme_colours);
  const labelToKey = Object.fromEntries(
    Object.entries(labels).map(([k, v]) => [v, k])
  );
  const byLabel = new Map();

  const upsert = (arr, field) => {
    (arr || []).forEach(({ key, label, value }) => {
      const k = key || Object.keys(labels).find((lk) => labels[lk] === label);
      const displayLabel = labels[k] || label || "(no label)";
      const row = byLabel.get(displayLabel) || {
        label: displayLabel,
        national: null,
        constituency: null,
        selected: null,
        color: "#6e6e6e",
      };
      const num = Number(value);
      row[field] = Number.isFinite(num) ? num : null;
      if (k && colors[k]) row.color = colors[k];
      byLabel.set(displayLabel, row);
    });
  };
  upsert(selectedFeatureData?.national, "national");
  upsert(selectedFeatureData?.constituency, "constituency");
  upsert(selectedFeatureData?.selected, "selected");
  upsert(selectedFeatureData?.unfilteredNational, "unfilteredNational");
  upsert(selectedFeatureData?.unfilteredConstituency, "unfilteredConstituency");
  upsert(selectedFeatureData?.unfilteredSelected, "unfilteredSelected");

  const toArrPol = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
  const orderedKeys =
    chartGroup?.type === "likert"
      ? (() => {
          const pos = toArrPol(chartGroup?.polarity?.pos);
          const neu = toArrPol(chartGroup?.polarity?.neu);
          const neg = toArrPol(chartGroup?.polarity?.neg);
          const dk = toArrPol(chartGroup?.polarity?.dk);
          const preferred = [...pos, ...neu, ...neg, ...dk];
          const seen = new Set(preferred.map(String));
          const rest = (chartGroup.keys || []).filter((k) => !seen.has(String(k)));
          return [...preferred, ...rest];
        })()
      : (chartGroup.keys || []);
  const order = orderedKeys.map((k) => labels[k] || k);
  const combined = sectorNetCombined ?? Array.from(byLabel.values()).sort((a, b) => {
    const ia = order.indexOf(a.label),
      ib = order.indexOf(b.label);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });
  window.__lastCombinedForYAxis = combined; // used by hook above

  const seriesAlmostEqual = (rows, a, b, tol = 0.01) => {
    let compared = false;
    for (const r of rows) {
      const va = r[a],
        vb = r[b];
      if (va == null && vb == null) continue;
      if (va == null || vb == null) return false;
      compared = true;
      if (Math.abs(va - vb) > tol) return false;
    }
    return compared;
  };
  const seriesAllNullishOrZero = (rows, key) =>
    rows.every(
      (r) => r[key] == null || !Number.isFinite(r[key]) || Math.abs(r[key]) < 1e-9
    );

  const selectedIsConstituency =
    typeof selectedFeatureLevel === "string" &&
    selectedFeatureLevel === "constituency";
  const hasNational = combined.some((d) => Number.isFinite(d.national));
  const hasConstituency = combined.some((d) =>
    Number.isFinite(d.constituency)
  );
  const hideSelected =
    // At constituency level we usually suppress "selected" because it duplicates
    // constituency values, but keep it if constituency aggregates are missing.
    (selectedIsConstituency && hasConstituency) ||
    seriesAlmostEqual(combined, "constituency", "selected", 0.01) ||
    seriesAllNullishOrZero(combined, "selected");

  const hasSelected =
    !hideSelected && combined.some((d) => Number.isFinite(d.selected));

  if (!hasNational && !hasConstituency && !hasSelected) return null;

  // ── Demographic comparison mode ────────────────────────────────────────────
  // When a demographic filter is active and unfiltered data exists for the
  // current area, show "Everyone here" vs "[Demographic] here" instead of the
  // normal national/constituency/selected breakdown.
  const hasDemoFilter = selectedDemographics &&
    Object.values(selectedDemographics).some(Boolean);
  const demoFilterLabel = (() => {
    if (!selectedDemographics) return null;
    const parts = Object.values(selectedDemographics).filter(Boolean);
    return parts.length ? parts.join(", ") : null;
  })();

  // Pick which unfiltered local series to use (most specific available)
  const unfilteredLocalKey = (() => {
    if (combined.some((d) => Number.isFinite(d.unfilteredSelected))) return "unfilteredSelected";
    if (combined.some((d) => Number.isFinite(d.unfilteredConstituency))) return "unfilteredConstituency";
    if (combined.some((d) => Number.isFinite(d.unfilteredNational))) return "unfilteredNational";
    return null;
  })();
  const filteredLocalKey = hasSelected ? "selected" : hasConstituency ? "constituency" : hasNational ? "national" : null;
  // When a filter is active but unfiltered data hasn't arrived yet for this area
  // (stale data from previous area), hold isDemoCompareMode=true to prevent
  // blinking back to the national-rep bar view during the transition.
  const unfilteredPending = hasDemoFilter && !unfilteredLocalKey && filteredLocalKey;
  const isDemoCompareMode = (hasDemoFilter && unfilteredLocalKey && filteredLocalKey) || unfilteredPending;

  const constituencyLabel =
    selectedFeatureLevel === "ward"
      ? selectedconstName || "Constituency"
      : selectedconstName || "Constituency";
  const selectedLabel = selectedAreaLabel || "Ward";
  const nValues = selectedFeatureData?.nValues || {};
  const fmtN = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)).toLocaleString() : "—");
  const showNRow =
    ActionMode === "filter" &&
    [nValues.national, nValues.constituency, nValues.selected].some((v) =>
      Number.isFinite(Number(v))
    );

  const getSortVal = (row) => {
    // In demo compare mode sort by the unfiltered local value (area overall)
    if (isDemoCompareMode && unfilteredLocalKey && Number.isFinite(row[unfilteredLocalKey])) return row[unfilteredLocalKey];
    if (hasSelected && Number.isFinite(row.selected)) return row.selected;
    if (hasConstituency && Number.isFinite(row.constituency)) return row.constituency;
    if (hasNational && Number.isFinite(row.national)) return row.national;
    return -Infinity;
  };

  // 🔹 Filter out rows where both constituency and selected are zero/null
// "an area is selected", as opposed to the national view — the name predates
// local authorities joining constituencies and wards as selectable areas.
const isConstituencyView =
  selectedFeatureLevel === "constituency" ||
  selectedFeatureLevel === "ward" ||
  selectedFeatureLevel === "local_authority";

// Area code for SNP/Plaid filtering (same logic as wave series IIFE below)
const _areaCodeRaw =
  selectedFeature?.value || selectedFeature?.code || selectedFeature?.id ||
  selectedFeature?.rowMatchIds?.[0] || selectedAreaId ||
  selectedFeature?.properties?.PCON24CD || selectedFeature?.properties?.WD24CD || null;
const _areaCode = _areaCodeRaw == null ? "" : String(_areaCodeRaw).trim().toUpperCase();
const _isScotland = _areaCode.length > 0 && _areaCode.startsWith("S");
const _isWales    = _areaCode.length > 0 && _areaCode.startsWith("W");
const _isVoteIntention = String(group?.label || "").toLowerCase().includes("vote intention");

let filteredData
filteredData = combined.filter((row) => {
  if (!isConstituencyView) return true; // keep all at national level

  // Suppress SNP in non-Scotland and Plaid in non-Wales for vote intention
  if (_isVoteIntention && _areaCode.length > 0) {
    const lbl = String(row.label || "").toLowerCase();
    if (!_isScotland && (lbl.includes("snp") || lbl.includes("scottish national"))) return false;
    if (!_isWales && lbl.includes("plaid")) return false;
  }

  const constituencyVal = Number(row.constituency);
  const selectedVal = Number(row.selected);
  const nationalVal = Number(row.national);
  const unfConstVal = Number(row.unfilteredConstituency);
  const unfSelVal   = Number(row.unfilteredSelected);
  const unfNatVal   = Number(row.unfilteredNational);

  const constituencyValid =
    Number.isFinite(constituencyVal) && Math.abs(constituencyVal) > 1e-6;
  const selectedValid =
    Number.isFinite(selectedVal) && Math.abs(selectedVal) > 1e-6;
  const nationalValid =
    Number.isFinite(nationalVal) && Math.abs(nationalVal) > 1e-6;
  const unfilteredValid =
    (Number.isFinite(unfConstVal) && Math.abs(unfConstVal) > 1e-6) ||
    (Number.isFinite(unfSelVal)   && Math.abs(unfSelVal)   > 1e-6) ||
    (Number.isFinite(unfNatVal)   && Math.abs(unfNatVal)   > 1e-6);

  // Keep rows when national-only data exists (common for modelled client questions).
  return constituencyValid || selectedValid || nationalValid || unfilteredValid;
})


const sortedData =
  chartGroup?.type === "likert"
    ? [...filteredData] // keep configured Likert order (pos -> neutral -> neg -> dk)
    : [...filteredData].sort((a, b) => getSortVal(b) - getSortVal(a));

// ---------- Dynamic y-axis width (bounded) ----------
const FONT = "12px Inter, Roboto, Arial, sans-serif";
const LINE_H = 14;
const TICK_PAD = 10;

// stable text measurer (one canvas)
const measure = (() => {
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");
  return (text) => {
    if (!ctx) return 0;
    ctx.font = FONT;
    return ctx.measureText(String(text ?? "")).width;
  };
})();

// ✅ dynamic but bounded so it never becomes "too wide"
const MIN_W = 110;
const MAX_W = 170; // adjust to taste
const longest = (sortedData || []).reduce(
  (m, d) => Math.max(m, measure(d.label)),
  0
);
const yAxisWidth = Math.min(MAX_W, Math.max(MIN_W, Math.ceil(longest + TICK_PAD)));
const maxPx = yAxisWidth - TICK_PAD;

// ---------- Wrap (max 2 lines + ellipsis ON LINE 2) ----------
const ellipsizeToWidth = (text, maxPx) => {
  const ell = "…";
  let s = String(text ?? "").trim();
  if (!s) return ell;

  if (measure(s + ell) <= maxPx) return s + ell;

  while (s.length && measure(s + ell) > maxPx) {
    s = s.slice(0, -1);
  }
  return (s.trimEnd() || "") + ell;
};

const wrapLabelFull = (text, maxPx) => {
  const s = String(text ?? "").trim();
  if (!s) return [""];
  if (measure(s) <= maxPx) return [s];

  const words = s.split(/\s+/).filter(Boolean);
  const lines = [];
  let current = "";

  for (const word of words) {
    const next = current ? `${current} ${word}` : word;
    if (measure(next) <= maxPx) {
      current = next;
    } else {
      if (current) lines.push(current);
      // single word too long — force it as its own line (no ellipsis)
      current = measure(word) <= maxPx ? word : ellipsizeToWidth(word, maxPx);
    }
  }
  if (current) lines.push(current);
  return lines.length ? lines : [s];
};

// Pre-wrap all labels once per render
const wrappedByLabel = new Map();
(sortedData || []).forEach((d) => {
  wrappedByLabel.set(d.label, wrapLabelFull(d.label, maxPx));
});

const TickWrap = ({ x, y, payload }) => {
  const text = String(payload?.value ?? "");
  const lines = wrappedByLabel.get(text) || wrapLabelFull(text, maxPx);
  // vertically centre the block around y
  const totalH = (lines.length - 1) * LINE_H;
  const startDy = -totalH / 2;

  return (
    <text
      x={x - 6}
      y={y}
      textAnchor="end"
      fontSize={12}
      fill={T.textMuted}
      style={{ pointerEvents: "none", fontFamily: "'DM Sans', sans-serif" }}
    >
      {lines.map((t, idx) => (
        <tspan key={idx} x={x - 6} dy={idx === 0 ? startDy : LINE_H}>
          {t}
        </tspan>
      ))}
    </text>
  );
};

// ---------- Stable height (prevents overlap) ----------
const seriesCount =
  [hasNational, hasConstituency, hasSelected].filter(Boolean).length || 1;

const BAR_SIZE = 10;
const BAR_GAP = 6;

// text height based on actual max wrapped line count
const maxLineCount = Math.max(1, ...[...(wrappedByLabel.values())].map(l => l.length));
const rowTextHeight = LINE_H * maxLineCount;

// bars stack height depends on series count
const rowBarsHeight = seriesCount * BAR_SIZE + (seriesCount - 1) * BAR_GAP;

const rowPad = 10;
const rowH = Math.max(rowTextHeight, rowBarsHeight) + rowPad;

const chartHeight = Math.max(260, (sortedData?.length || 0) * rowH + 30);

// ---------- your existing min/max (keep as-is) ----------
const [minVal, maxVal] = (() => {
  const vals = sortedData
    .flatMap((d) => [
      hasNational ? d.national : null,
      hasConstituency ? d.constituency : null,
      hasSelected ? d.selected : null,
      Number.isFinite(d.unfilteredNational) ? d.unfilteredNational : null,
      Number.isFinite(d.unfilteredConstituency) ? d.unfilteredConstituency : null,
      Number.isFinite(d.unfilteredSelected) ? d.unfilteredSelected : null,
    ])
    .filter(Number.isFinite);

  if (!vals.length) return [0, 100];

  const rawMin = Math.min(...vals);
  const rawMax = Math.max(...vals);

  const lower = (group?.type === "price_elast" || sectorNetCombined)
    ? Math.floor(Math.min(0, rawMin))
    : 0;

  const upper =
    group?.type === "price_elast"
      ? Math.ceil(rawMax)
      : Math.min(100, Math.ceil(Math.max(10, rawMax)));

  return [lower, upper];
})();


  

  // ── Plain-language summary ────────────────────────────────────────────────
  const plainSummary = (() => {
    if (!sortedData?.length) return null;
    const isLikert = chartGroup?.type === "likert";
    const isLikertGrid = group?.type === "likert_grid";
    const fmt = (v) => (Number.isFinite(v) ? `${v.toFixed(1)}%` : null);
    const fmtPp = (v) => v >= 0 ? `+${v.toFixed(1)}pp` : `${v.toFixed(1)}pp`;

    // Pick the most local value available for a row.
    // When demo compare mode is active, use the unfiltered local value so the
    // plain summary reflects everyone in the area (not the filtered group).
    const localVal = (row) => {
      if (isDemoCompareMode) {
        if (unfilteredLocalKey && Number.isFinite(row[unfilteredLocalKey])) return row[unfilteredLocalKey];
      }
      if (hasSelected && Number.isFinite(row.selected)) return row.selected;
      if (hasConstituency && Number.isFinite(row.constituency)) return row.constituency;
      if (hasNational && Number.isFinite(row.national)) return row.national;
      return null;
    };

    // Context: are we looking at a specific area or nationally?
    const isWardLevel = selectedFeatureLevel === "ward";
    const wardName = isWardLevel ? (selectedFeature?.name || null) : null;
    const inArea = wardName || selectedconstName || null;

    // Active demographic filter label (e.g. "women", "18–34s")
    const demoContext = (() => {
      if (!selectedDemographics) return null;
      const parts = [];
      Object.entries(selectedDemographics).forEach(([, label]) => {
        if (label) parts.push(label);
      });
      return parts.length ? parts.join(", ") : null;
    })();

    if (isLikert) {
      const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
      const posKeys = toArr(chartGroup?.polarity?.pos);
      const negKeys = toArr(chartGroup?.polarity?.neg);
      const sumPol = (keys) => keys.reduce((s, k) => {
        const row = sortedData.find((r) => (labelToKey[r.label] || r.label) === k);
        const v = row ? localVal(row) : null;
        return s + (Number.isFinite(v) ? v : 0);
      }, 0);
      const hasPosData = posKeys.some((k) => {
        const row = sortedData.find((r) => (labelToKey[r.label] || r.label) === k);
        return row && Number.isFinite(localVal(row));
      });
      if (!hasPosData) return null;
      const pos = sumPol(posKeys);
      const neg = sumPol(negKeys);
      const net = pos - neg;

      const netFor = (key) => {
        const p = posKeys.reduce((s, k) => {
          const row = sortedData.find((r) => (labelToKey[r.label] || r.label) === k);
          return s + (Number.isFinite(row?.[key]) ? row[key] : 0);
        }, 0);
        const n = negKeys.reduce((s, k) => {
          const row = sortedData.find((r) => (labelToKey[r.label] || r.label) === k);
          return s + (Number.isFinite(row?.[key]) ? row[key] : 0);
        }, 0);
        return p - n;
      };

      let vsConst = null;
      if (isWardLevel && hasConstituency) {
        const d = net - netFor("constituency");
        if (Math.abs(d) >= 2) vsConst = d;
      }
      let vsNational = null;
      if (inArea && hasNational) {
        const d = net - netFor("national");
        if (Math.abs(d) >= 2) vsNational = d;
      }

      const whoStr = (!isDemoCompareMode && demoContext) ? `Among ${demoContext}` : inArea ? `In ${inArea}` : "Nationally";
      let text;
      if (net > 5) {
        text = `${whoStr}, ${fmt(pos)} hold a positive view — a net score of ${fmtPp(net)}.`;
      } else if (net < -5) {
        text = `${whoStr}, sentiment is negative: ${fmt(neg)} hold a negative view (net ${fmtPp(net)}).`;
      } else {
        text = `${whoStr}, views are evenly split — ${fmt(pos)} positive, ${fmt(neg)} negative.`;
      }
      if (vsConst !== null) {
        text += ` That's ${fmtPp(vsConst)} ${vsConst > 0 ? "above" : "below"} the ${selectedconstName || "constituency"} average`;
        text += vsNational !== null
          ? ` and ${fmtPp(vsNational)} ${vsNational > 0 ? "above" : "below"} the national average.`
          : ".";
      } else if (vsNational !== null) {
        text += ` That's ${fmtPp(vsNational)} ${vsNational > 0 ? "above" : "below"} the national average.`;
      }
      return text;
    }

    if (isLikertGrid) {
      if (!sectorNetCombined?.length) return null;
      const withVals = sectorNetCombined.map((s) => ({
        ...s,
        val: hasSelected && Number.isFinite(s.selected) ? s.selected
          : hasConstituency && Number.isFinite(s.constituency) ? s.constituency
          : s.national,
      })).filter((s) => Number.isFinite(s.val)).sort((a, b) => b.val - a.val);
      if (!withVals.length) return null;
      const top = withVals[0];
      const bottom = withVals[withVals.length - 1];
      const whoStr = inArea ? `In ${inArea}` : "Nationally";
      let text = `${whoStr}, ${top.label} has the most support (${top.val > 0 ? "+" : ""}${top.val.toFixed(1)}% net).`;
      if (bottom.label !== top.label && bottom.val < top.val - 10) {
        text += ` ${bottom.label} is considerably weaker at ${bottom.val > 0 ? "+" : ""}${bottom.val.toFixed(1)}%.`;
      }
      return text;
    }

    // Categorical (vote intention, issues, etc.)
    const withVals = sortedData.map((r) => ({ ...r, val: localVal(r) })).filter((r) => Number.isFinite(r.val));
    if (!withVals.length) return null;
    const sorted = [...withVals].sort((a, b) => b.val - a.val);
    const top = sorted[0];
    const second = sorted[1];

    const whoStr = (!isDemoCompareMode && demoContext) ? `Among ${demoContext}` : inArea ? `In ${inArea}` : "Nationally";

    // vs-constituency diff (ward level) and vs-national diff
    let vsConst = null;
    if (isWardLevel && hasConstituency && Number.isFinite(top.constituency)) {
      const d = (top.val ?? 0) - top.constituency;
      if (Math.abs(d) >= 2) vsConst = d;
    }
    let diff = null;
    if (inArea && hasNational && Number.isFinite(top.national)) {
      const d = (top.val ?? 0) - top.national;
      if (Math.abs(d) >= 2) diff = d;
    }

    let text;
    if (second && fmt(second.val)) {
      text = `${whoStr}, ${top.label} leads at ${fmt(top.val)}, ahead of ${second.label} at ${fmt(second.val)}.`;
    } else {
      text = `${whoStr}, ${top.label} is the leading response at ${fmt(top.val)}.`;
    }
    if (vsConst !== null) {
      text += ` That's ${fmtPp(vsConst)} ${vsConst > 0 ? "above" : "below"} the ${selectedconstName || "constituency"} average`;
      if (diff !== null) {
        text += ` and ${fmtPp(diff)} ${diff > 0 ? "above" : "below"} the national average.`;
      } else {
        text += ".";
      }
    } else if (diff !== null) {
      text += ` ${top.label} is ${fmtPp(diff)} ${diff > 0 ? "above" : "below"} the national average here.`;
    }
    return text;
  })();

  // ── Demo compare gap sentence ──────────────────────────────────────────────
  // When a demographic filter is active and unfiltered local data is available,
  // compute the net/top gap between the filtered group and everyone in the area.
  const demoGapSentence = (() => {
    if (!isDemoCompareMode) return null;
    const isLikert = chartGroup?.type === "likert";
    const fmtPp2 = (v) => v >= 0 ? `+${v.toFixed(1)}pp` : `${v.toFixed(1)}pp`;

    if (isLikert) {
      const toArr = (v) => Array.isArray(v) ? v : v != null ? [v] : [];
      const posKeys = toArr(chartGroup?.polarity?.pos);
      const negKeys = toArr(chartGroup?.polarity?.neg);
      const sumFrom = (key) => [...posKeys, ...negKeys].reduce((acc, k) => {
        const row = sortedData.find((r) => (labelToKey[r.label] || r.label) === k);
        return { pos: acc.pos + (Number.isFinite(row?.[key]) && posKeys.includes(k) ? row[key] : 0),
                 neg: acc.neg + (Number.isFinite(row?.[key]) && negKeys.includes(k) ? row[key] : 0) };
      }, { pos: 0, neg: 0 });
      const filt = sumFrom(filteredLocalKey);
      const unf = sumFrom(unfilteredLocalKey);
      const filtNet = filt.pos - filt.neg;
      const unfNet = unf.pos - unf.neg;
      const gap = filtNet - unfNet;
      if (!Number.isFinite(gap) || Math.abs(gap) < 1) return null;
      const likertGroupRef = demoFilterLabel && demoFilterLabel.split(",").length === 1
        ? demoFilterLabel : "this group";
      return `Among ${likertGroupRef}, sentiment is ${fmtPp2(gap)} ${gap > 0 ? "more positive" : "more negative"} than the area overall.`;
    }

    // Categorical — compare top response for filtered group vs overall area
    // Use all rows that have EITHER filtered or unfiltered data so we don't
    // lose the leading party just because one slice is sparse.
    const withFilt = sortedData.map((r) => ({ label: r.label, val: r[filteredLocalKey] })).filter((r) => Number.isFinite(r.val));
    const withUnf  = sortedData.map((r) => ({ label: r.label, val: r[unfilteredLocalKey] })).filter((r) => Number.isFinite(r.val));
    if (!withFilt.length || !withUnf.length) return null;
    const topFilt = [...withFilt].sort((a, b) => b.val - a.val)[0];
    const topUnf  = [...withUnf].sort((a, b) => b.val - a.val)[0];
    // Fall back to 0 if unfiltered has no entry for the top filtered party
    const matchUnf = withUnf.find((r) => r.label === topFilt.label);
    const unfVal = matchUnf ? matchUnf.val : 0;
    const gap = topFilt.val - unfVal;
    if (Math.abs(gap) < 0.5) return null;
    const differsFrom = topFilt.label !== topUnf.label
      ? ` (the area overall leans ${topUnf.label})`
      : "";
    const dir = gap > 0 ? "higher" : "lower";
    // Use "this group" if the party name repeats the filter label, or if there
    // are multiple filters (long label reads poorly mid-sentence).
    const filterParts = demoFilterLabel ? demoFilterLabel.split(",").length : 0;
    const groupRef = !demoFilterLabel || filterParts > 1 ||
      demoFilterLabel.toLowerCase().includes(topFilt.label.toLowerCase())
        ? "this group"
        : demoFilterLabel;
    return `Among ${groupRef}, ${topFilt.label} support is ${fmtPp2(Math.abs(gap))} ${dir} than the constituency overall${differsFrom}.`;
  })();

  return (
    <StyledAccordion id="tour-compare-chart" defaultExpanded>
      <StyledAccordionSummary>
        <SectionLabel>Compare by Geographies</SectionLabel>
      </StyledAccordionSummary>

      <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
        {/* Wave series setup */}
        {(() => {
          const rawWaveSeries = Array.isArray(selectedFeatureData?.waveSeries) ? selectedFeatureData.waveSeries : [];
          const keyLabels = selectedFeatureData?.waveSeriesKeyLabels || {};
          const keyColors = selectedFeatureData?.waveSeriesKeyColors || {};
          const getKeyLabel = (k) => keyLabels[k] || labels[k] || k;
          const getKeyColor = (k) => keyColors[k] || colors[k] || T.accent;
          const isVoteIntention = String(group?.label || "").toLowerCase().includes("vote intention");
          const areaCodeRaw =
            selectedFeature?.value ||
            selectedFeature?.code ||
            selectedFeature?.id ||
            selectedFeature?.rowMatchIds?.[0] ||
            selectedAreaId ||
            selectedFeature?.properties?.PCON24CD ||
            selectedFeature?.properties?.WD24CD ||
            null;
          const areaCode = areaCodeRaw == null ? "" : String(areaCodeRaw).trim().toUpperCase();
          const hasAreaCode = areaCode.length > 0;
          const isScotland = hasAreaCode && areaCode.startsWith("S");
          const isWales = hasAreaCode && areaCode.startsWith("W");
          const isDontKnowOption = (k) => {
            const kk = String(k || "").toLowerCase();
            const lbl = String(getKeyLabel(k) || "").toLowerCase();
            return kk.includes("dontknow") || lbl.includes("don't know") || lbl.includes("dont know");
          };
          const isSnpOption = (k) => {
            const kk = String(k || "").toLowerCase();
            const lbl = String(getKeyLabel(k) || "").toLowerCase();
            return kk.includes("snp") || lbl.includes(" scottish national party") || lbl.startsWith("snp") || lbl.includes(" snp ");
          };
          const isPlaidOption = (k) => {
            const kk = String(k || "").toLowerCase();
            const lbl = String(getKeyLabel(k) || "").toLowerCase();
            return kk.includes("plaid") || lbl.includes("plaid cymru");
          };
          const allWaveKeys = (Array.isArray(selectedFeatureData?.waveSeriesKeys)
            ? selectedFeatureData.waveSeriesKeys
            : (group?.keys || [])
          ).filter((k) => {
            if (!isVoteIntention) return true;
            if (isDontKnowOption(k)) return false;
            if (hasAreaCode && !isScotland && isSnpOption(k)) return false;
            if (hasAreaCode && !isWales && isPlaidOption(k)) return false;
            return true;
          });
          // Which keys are toggled on (null = all)
          const visibleKeys = activeWaveKeys
            ? allWaveKeys.filter((k) => activeWaveKeys.has(k))
            : allWaveKeys;
          const effectiveKeys = visibleKeys.length > 0 ? visibleKeys : allWaveKeys;
          const trendWaveSeries = rawWaveSeries.filter((row) => {
            const vals = effectiveKeys
              .map((k) => Number(row?.[k]))
              .filter((v) => Number.isFinite(v));
            if (!vals.length) return false;
            // Some waves are returned as zero-filled placeholders when no data exists.
            // Hide those waves so the trend chart only shows real observations.
            return vals.some((v) => Math.abs(v) > 1e-9);
          });
          const movementRows = effectiveKeys
            .map((k) => {
              const points = trendWaveSeries
                .map((r) => ({ wave: Number(r?.wave), value: Number(r?.[k]) }))
                .filter((p) => Number.isFinite(p.wave) && Number.isFinite(p.value));
              if (points.length < 2) return null;
              const first = points[0];
              const last = points[points.length - 1];
              const delta = last.value - first.value;
              return {
                key: k,
                label: getKeyLabel(k),
                color: getKeyColor(k),
                startWave: first.wave,
                endWave: last.wave,
                startValue: first.value,
                endValue: last.value,
                delta,
                absDelta: Math.abs(delta),
              };
            })
            .filter(Boolean)
            .sort((a, b) => b.absDelta - a.absDelta);
          const topUp = movementRows
            .filter((r) => r.delta > 0)
            .sort((a, b) => b.delta - a.delta)[0] || null;
          const topDown = movementRows
            .filter((r) => r.delta < 0)
            .sort((a, b) => a.delta - b.delta)[0] || null;
          const showTrendToggle = compareWaves && trendWaveSeries.length >= 2 && allWaveKeys.length > 0;
          const isTrendMode = showTrendToggle && compareChartMode === "trend";

          const toggleKey = (k) => {
            if (!activeWaveKeys) {
              // Currently all active — deactivate all except this one
              setActiveWaveKeys(new Set([k]));
            } else {
              const next = new Set(activeWaveKeys);
              if (next.has(k)) {
                next.delete(k);
                if (next.size === 0) {
                  setActiveWaveKeys(null); // reset to all when last one removed
                } else {
                  setActiveWaveKeys(next);
                }
              } else {
                next.add(k);
                if (next.size === allWaveKeys.length) {
                  setActiveWaveKeys(null); // all active again
                } else {
                  setActiveWaveKeys(next);
                }
              }
            }
          };

          return (
            <>
              {/* Plain-language summary + trend callout */}
              {(() => {
                // Build trend callout from movementRows (already computed above)
                const trendCallout = (() => {
                  if (!compareWaves || movementRows.length === 0) return null;
                  const waveNums = trendWaveSeries.map((r) => Number(r?.wave)).filter(Number.isFinite);
                  if (waveNums.length < 2) return null;
                  const firstWave = Math.min(...waveNums);
                  const lastWave = Math.max(...waveNums);
                  const fmtWave = (w) => {
                    const s = String(w);
                    if (s.length !== 4) return `Wave ${w}`;
                    const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                    const yr = `20${s.slice(0, 2)}`;
                    const mo = parseInt(s.slice(2), 10);
                    return `${months[mo - 1] || s.slice(2)} ${yr}`;
                  };
                  const parts = [];
                  if (topUp && Math.abs(topUp.delta) >= 1) {
                    parts.push(`"${topUp.label}" is up ${topUp.delta.toFixed(1)}pp since ${fmtWave(firstWave)}.`);
                  }
                  if (topDown && Math.abs(topDown.delta) >= 1) {
                    parts.push(`"${topDown.label}" is down ${Math.abs(topDown.delta).toFixed(1)}pp over the same period.`);
                  }
                  if (!parts.length) return null;
                  const trendPrefix = demoFilterLabel
                    ? `Among ${demoFilterLabel.split(",").length === 1 ? demoFilterLabel : "this group"}, trend`
                    : "Trend";
                  return `${trendPrefix} (${fmtWave(firstWave)} → ${fmtWave(lastWave)}): ${parts.join(" ")}`;
                })();

                const showPlain = plainSummary && !isTrendMode;
                const showDemo = demoGapSentence && !isTrendMode;
                if (!showPlain && !trendCallout && !showDemo) return null;
                return (
                  <Box sx={{
                    mb: 1.5, p: 1.5,
                    borderRadius: "8px",
                    bgcolor: T.accentDim,
                    border: `1px solid ${T.chipActiveBorder}`,
                  }}>
                    {showPlain && (
                      <Typography sx={{ fontSize: "12px", color: T.text, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55 }}>
                        {plainSummary}
                      </Typography>
                    )}
                    {showDemo && (
                      <Typography sx={{ fontSize: "12px", color: "#7c3aed", fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, mt: showPlain ? 0.75 : 0, fontWeight: 600 }}>
                        {demoGapSentence}
                      </Typography>
                    )}
                    {trendCallout && (
                      <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.55, mt: (showPlain || showDemo) ? 0.75 : 0, fontStyle: "italic" }}>
                        {trendCallout}
                      </Typography>
                    )}
                  </Box>
                );
              })()}

              {/* Legend (bar mode) */}
              {!isTrendMode && (
                <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}>
                  {(() => {
                    const legendItems = isDemoCompareMode
                      ? [
                          { label: `Everyone ${unfilteredLocalKey === "unfilteredNational" ? "nationally" : `in ${selectedconstName || "area"}`}`, opacity: 0.4, color: T.accent },
                          { label: demoFilterLabel, opacity: 1, color: "#7c3aed" },
                        ]
                      : [
                          hasNational && { label: "National", opacity: 0.35, color: T.accent },
                          hasConstituency && { label: constituencyLabel, opacity: 0.65, color: T.accent },
                          hasSelected && { label: selectedLabel, opacity: 1, color: T.accent },
                        ].filter(Boolean);
                    return legendItems.map(({ label, opacity, color }) => (
                      <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: "2px", bgcolor: color, opacity }} />
                        <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>{label}</Typography>
                      </Box>
                    ));
                  })()}
                </Stack>
              )}

              {/* Bars / Trend toggle */}
              {showTrendToggle && (
                <Stack direction="row" spacing={0.5} sx={{ mb: 1 }}>
                  {["bars", "trend"].map((mode) => {
                    const active = compareChartMode === mode;
                    return (
                      <ButtonBase
                        key={mode}
                        onClick={() => setCompareChartMode(mode)}
                        sx={{
                          px: 1.25, py: 0.5,
                          borderRadius: "6px",
                          border: "1px solid",
                          borderColor: active ? T.chipActiveBorder : T.border,
                          bgcolor: active ? T.chipActive : T.chip,
                          color: active ? T.accent : T.textMuted,
                          fontSize: "11px",
                          fontWeight: active ? 600 : 400,
                          fontFamily: "'DM Sans', sans-serif",
                          letterSpacing: "0.02em",
                          transition: "all 0.15s ease",
                          "&:hover": {
                            borderColor: active ? T.chipActiveBorder : T.borderStrong,
                            bgcolor: active ? T.chipActive : T.surfaceHover,
                            color: active ? T.accent : T.text,
                          },
                        }}
                      >
                        {mode.charAt(0).toUpperCase() + mode.slice(1)}
                      </ButtonBase>
                    );
                  })}
                </Stack>
              )}

              {/* vs national average toggle — only when an area is selected */}
              {!isTrendMode && (hasConstituency || hasSelected) && hasNational && (
                <ButtonBase
                  onClick={() => setVsAvgMode((v) => !v)}
                  sx={{
                    mb: 1,
                    px: 1.25, py: 0.5,
                    borderRadius: "6px",
                    border: "1px solid",
                    borderColor: vsAvgMode ? "#16a34a" : T.border,
                    bgcolor: vsAvgMode ? "#f0fdf4" : T.chip,
                    color: vsAvgMode ? "#16a34a" : T.textMuted,
                    fontSize: "11px",
                    fontWeight: vsAvgMode ? 600 : 400,
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "0.02em",
                    transition: "all 0.15s ease",
                    alignSelf: "flex-start",
                    "&:hover": {
                      borderColor: vsAvgMode ? "#16a34a" : T.borderStrong,
                      bgcolor: vsAvgMode ? "#dcfce7" : T.surfaceHover,
                      color: vsAvgMode ? "#16a34a" : T.text,
                    },
                  }}
                >
                  {isDemoCompareMode ? "± vs national (everyone)" : "± vs national avg"}
                </ButtonBase>
              )}

              {/* Trend chart */}
              {isTrendMode && (() => {
                // Build demographic filter pills from selectedDemographics
                const demoPills = [];
                if (selectedDemographics) {
                  Object.entries(selectedDemographics).forEach(([key, val]) => {
                    const lbls = Array.isArray(val) ? val : val ? [val] : [];
                    lbls.forEach((l) => demoPills.push(`${key.charAt(0).toUpperCase() + key.slice(1)}: ${l}`));
                  });
                }
                const hasActiveFilters = demoPills.length > 0;

                // Determine if national line data exists for these keys
                const natKey = (k) => `${k}__nat`;
                const hasNationalData = effectiveKeys.some((k) =>
                  trendWaveSeries.some((r) => Number.isFinite(Number(r?.[natKey(k)])))
                );

                return (
                <>
                  {/* Demographic filter banner */}
                  {hasActiveFilters && (
                    <Box sx={{
                      mb: 1.25,
                      borderRadius: 2,
                      overflow: "hidden",
                      border: "1.5px solid rgba(245,158,11,0.45)",
                      boxShadow: "0 0 0 3px rgba(245,158,11,0.08)",
                    }}>
                      {/* Title bar */}
                      <Box sx={{
                        px: 1.25, py: 0.6,
                        bgcolor: "rgba(245,158,11,0.13)",
                        borderBottom: "1px solid rgba(245,158,11,0.25)",
                        display: "flex", alignItems: "center", gap: 0.6,
                      }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 800, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                          ⚠ Filtered view
                        </Typography>
                      </Box>
                      {/* Filter pills */}
                      <Box sx={{ px: 1.25, py: 0.75, bgcolor: "rgba(245,158,11,0.06)", display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {demoPills.map((pill) => (
                          <Box key={pill} sx={{
                            fontSize: "11px", fontWeight: 600, color: "#78350f",
                            bgcolor: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.35)",
                            borderRadius: "5px", px: 0.9, py: 0.3, lineHeight: 1.6,
                            fontFamily: "'DM Sans', sans-serif",
                          }}>
                            {pill}
                          </Box>
                        ))}
                      </Box>
                      {/* Note */}
                      <Box sx={{ px: 1.25, pb: 0.75, bgcolor: "rgba(245,158,11,0.06)" }}>
                        <Typography sx={{ fontSize: "10px", color: "#92400e", opacity: 0.85, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.5 }}>
                          Trend and national comparison reflect this filtered group only.
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Category toggle chips + vs National toggle */}
                  {allWaveKeys.length > 1 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: "0.65rem", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.5 }}>
                        Show {String(group?.label || "categories").split(" ").pop().toLowerCase()}
                      </Typography>
                      <ToggleButtonGroup
                        value={activeWaveKeys ? Array.from(activeWaveKeys) : allWaveKeys}
                        onChange={(_e, newVal) => {
                          if (!newVal || newVal.length === 0 || newVal.length === allWaveKeys.length) {
                            setActiveWaveKeys(null);
                          } else {
                            setActiveWaveKeys(new Set(newVal));
                          }
                        }}
                        size="small"
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          borderRadius: 2,
                          p: 0.3,
                          gap: 0.25,
                          "& .MuiToggleButton-root": {
                            border: "none",
                            borderRadius: 1.5,
                            py: 0.3,
                            px: 0.8,
                            textTransform: "none",
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            color: "text.secondary",
                            minWidth: 0,
                            lineHeight: 1.3,
                            fontFamily: "'DM Sans', sans-serif",
                            "&.Mui-selected": {
                              backgroundColor: "#fff",
                              color: "text.primary",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                              "&:hover": { backgroundColor: "#fff" },
                            },
                            "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                          },
                        }}
                      >
                        {allWaveKeys.map((k) => (
                          <ToggleButton key={k} value={k}>
                            {getKeyColor(k) && (
                              <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor(k), mr: 0.5, flexShrink: 0 }} />
                            )}
                            {getKeyLabel(k)}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </Box>
                  )}

                  {/* Chart header row: title + "vs National" toggle */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.75 }}>
                    <Typography sx={{ fontSize: "11px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                      Trend across waves (%)
                    </Typography>
                    {hasNationalData && (
                      <ButtonBase
                        onClick={() => setShowNationalLine((v) => !v)}
                        sx={{
                          px: 1, py: 0.35,
                          borderRadius: "5px",
                          border: "1px solid",
                          borderColor: showNationalLine ? T.chipActiveBorder : T.border,
                          bgcolor: showNationalLine ? T.chipActive : T.chip,
                          color: showNationalLine ? T.accent : T.textMuted,
                          fontSize: "10px",
                          fontWeight: showNationalLine ? 600 : 400,
                          fontFamily: "'DM Sans', sans-serif",
                          display: "flex", alignItems: "center", gap: 0.5,
                          transition: "all 0.15s ease",
                          "&:hover": { borderColor: T.borderStrong, bgcolor: T.surfaceHover, color: T.text },
                        }}
                      >
                        <Box component="span" sx={{
                          display: "inline-block", width: 14, height: 0,
                          borderTop: "2px dashed currentColor", verticalAlign: "middle", mr: 0.4,
                        }} />
                        vs National
                      </ButtonBase>
                    )}
                  </Box>

                  {/* Chart line style legend */}
                  {showNationalLine && hasNationalData && (
                    <Box sx={{ display: "flex", gap: 1.5, mb: 0.75, flexWrap: "wrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Box sx={{ width: 16, height: 2, bgcolor: T.textMuted, borderRadius: 1 }} />
                        <Typography sx={{ fontSize: "10px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                          {selectedAreaLabel || "Constituency"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <svg width="16" height="4"><line x1="0" y1="2" x2="16" y2="2" stroke={T.textMuted} strokeWidth="1.5" strokeDasharray="3 2" /></svg>
                        <Typography sx={{ fontSize: "10px", color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                          National
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart
                      key={`${selectedVariable}-${selectedAreaLabel}`}
                      data={trendWaveSeries}
                      margin={{ top: 4, right: 16, left: 8, bottom: 4 }}
                      onMouseLeave={() => setHoveredTrendKey(null)}
                    >
                      <XAxis dataKey="wave" tick={{ fontSize: 11, fill: T.textMuted }} tickFormatter={(v) => `Wave ${v}`} />
                      <YAxis
                        tick={{ fontSize: 10, fill: T.textMuted }}
                        width={40}
                        tickFormatter={(v) => `${v.toFixed(0)}%`}
                        domain={["auto", "auto"]}
                      />
                      <CartesianGrid stroke={T.border} strokeDasharray="3 3" opacity={0.5} />
                      <RechartsTooltip
                        content={({ active, payload, label: ttLabel }) => {
                          if (!active || !payload?.length) return null;
                          const fmt = (v) => v == null || isNaN(v) ? "—" : `${v.toFixed(1)}%`;
                          const waveIdx = trendWaveSeries.findIndex((r) => Number(r?.wave) === Number(ttLabel));
                          // numOrNull, not Number(): an option the wave didn't
                          // offer is null here, and Number(null) is 0 — which
                          // read as a real "0.0%" for every candidate added or
                          // dropped mid-series.
                          const valueAt = (idx, k) => numOrNull(trendWaveSeries[idx]?.[k]);
                          const deltaForKey = (k) => {
                            if (waveIdx <= 0) return null;
                            const prev = valueAt(waveIdx - 1, k);
                            const curr = valueAt(waveIdx, k);
                            return prev != null && curr != null ? curr - prev : null;
                          };
                          // Show only focused key or all visible keys, and only
                          // where this wave has a value — an option added or
                          // removed mid-series is absent from the waves either
                          // side of its run, not sitting at zero.
                          const focused = hoveredTrendKey && effectiveKeys.includes(hoveredTrendKey) ? hoveredTrendKey : null;
                          const visKeys = (focused ? [focused] : effectiveKeys)
                            .filter((k) => valueAt(waveIdx, k) != null);
                          if (!visKeys.length) return null;
                          return (
                            <div style={{
                              background: "#fff",
                              border: "1px solid rgba(0,0,0,0.1)",
                              borderRadius: 8,
                              padding: "8px 12px",
                              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                              fontFamily: "'DM Sans', system-ui, sans-serif",
                              minWidth: 160,
                            }}>
                              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", marginBottom: 7, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                                Wave {ttLabel}
                              </div>
                              {visKeys.map((k) => {
                                const val = valueAt(waveIdx, k);
                                const delta = deltaForKey(k);
                                const natVal = showNationalLine ? valueAt(waveIdx, natKey(k)) : null;
                                const dColor = delta > 0 ? "#16a34a" : delta < 0 ? "#dc2626" : "#9ca3af";
                                return (
                                  <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                                      <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: getKeyColor(k), flexShrink: 0 }} />
                                      <span style={{ fontSize: 12, color: "#374151", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {getKeyLabel(k)}
                                      </span>
                                    </div>
                                    <div style={{ display: "flex", alignItems: "baseline", gap: 5, flexShrink: 0 }}>
                                      <span style={{ fontSize: 12, fontWeight: 700, color: "#1a1d23" }}>
                                        {fmt(val)}
                                      </span>
                                      {Number.isFinite(delta) && (
                                        <span style={{ fontSize: 10.5, fontWeight: 700, color: dColor }}>
                                          {delta > 0 ? "+" : ""}{delta.toFixed(1)}pp
                                        </span>
                                      )}
                                      {Number.isFinite(natVal) && (
                                        <span style={{ fontSize: 10, color: "#9ca3af", fontWeight: 500 }}>
                                          ({fmt(natVal)} nat)
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          );
                        }}
                      />
                      {/* Primary constituency/area lines */}
                      {effectiveKeys.map((k) => (
                        <React.Fragment key={k}>
                          {/* Wide invisible hit area for hover */}
                          <Line
                            type="monotone"
                            dataKey={k}
                            stroke="rgba(0,0,0,0)"
                            strokeWidth={14}
                            dot={false}
                            activeDot={false}
                            connectNulls
                            isAnimationActive={false}
                            onMouseOver={() => setHoveredTrendKey(k)}
                            onMouseOut={() => setHoveredTrendKey(null)}
                          />
                          <Line
                            type="monotone"
                            dataKey={k}
                            stroke={getKeyColor(k)}
                            strokeWidth={hoveredTrendKey === k ? 3 : 2}
                            strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.2 : 1}
                            dot={(dotProps) => {
                              const { cx, cy, value } = dotProps || {};
                              if (!Number.isFinite(cx) || !Number.isFinite(cy) || !Number.isFinite(Number(value))) return null;
                              const isFocused = hoveredTrendKey === k;
                              return (
                                <circle cx={cx} cy={cy} r={isFocused ? 3 : 2}
                                  fill={getKeyColor(k)} stroke="#fff" strokeWidth={1}
                                  style={{ cursor: "pointer" }}
                                  onMouseEnter={() => setHoveredTrendKey(k)}
                                  onMouseLeave={() => setHoveredTrendKey(null)}
                                />
                              );
                            }}
                            activeDot={{ r: 4 }}
                            isAnimationActive={trendAnimate}
                            animationDuration={450}
                            connectNulls
                            onMouseOver={() => setHoveredTrendKey(k)}
                            onMouseOut={() => setHoveredTrendKey(null)}
                          />
                          {/* National comparison line — dashed, same color, 50% opacity */}
                          {showNationalLine && hasNationalData && (
                            <Line
                              key={natKey(k)}
                              type="monotone"
                              dataKey={natKey(k)}
                              stroke={getKeyColor(k)}
                              strokeWidth={1.5}
                              strokeDasharray="4 3"
                              strokeOpacity={hoveredTrendKey && hoveredTrendKey !== k ? 0.12 : 0.5}
                              dot={false}
                              activeDot={false}
                              connectNulls
                              isAnimationActive={false}
                            />
                          )}
                        </React.Fragment>
                      ))}
                    </LineChart>
                  </ResponsiveContainer>

                </>
                );
              })()}

              {/* All-party movement list — replaces bar chart in compare/trend mode */}
              {isTrendMode && movementRows.length > 0 && (() => {
                const waveNums = trendWaveSeries.map((r) => Number(r?.wave)).filter(Number.isFinite);
                const waveRangeLabel = waveNums.length >= 2
                  ? `Wave ${Math.min(...waveNums)} → Wave ${Math.max(...waveNums)}`
                  : null;
                // Re-sort: gainers first (desc delta), then losers (asc delta)
                const sortedMovement = [...movementRows].sort((a, b) => b.delta - a.delta);
                return (
                  <Box sx={{ mt: 1.5, border: `1px solid ${T.border}`, borderRadius: 2, overflow: "hidden" }}>
                    {/* Header */}
                    <Box sx={{
                      px: 1.5, py: 0.9, bgcolor: T.surface,
                      borderBottom: `1px solid ${T.border}`,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        Movement
                      </Typography>
                      {waveRangeLabel && (
                        <Typography sx={{
                          fontSize: "9.5px", fontWeight: 600, color: "primary.main",
                          bgcolor: "rgba(25,118,210,0.07)", border: "1px solid rgba(25,118,210,0.15)",
                          borderRadius: "4px", px: 0.75, py: 0.25, lineHeight: 1.5,
                        }}>
                          {waveRangeLabel}
                        </Typography>
                      )}
                    </Box>
                    {/* Column labels */}
                    <Box sx={{
                      px: 1.5, py: 0.5, display: "flex", alignItems: "center",
                      borderBottom: `1px solid ${T.border}`, bgcolor: "#fafbfc",
                    }}>
                      <Typography sx={{ flex: 1, fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        {String(group?.label || "Category").split(" ").pop()}
                      </Typography>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 44, textAlign: "right" }}>
                        Latest
                      </Typography>
                      <Typography sx={{ fontSize: "9.5px", fontWeight: 700, color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.06em", minWidth: 54, textAlign: "right" }}>
                        Change
                      </Typography>
                    </Box>
                    {/* Rows */}
                    {sortedMovement.map((row, i) => {
                      const isGainer = row.delta > 0.05;
                      const isLoser  = row.delta < -0.05;
                      const rowBg = isGainer
                        ? "rgba(22,163,74,0.04)"
                        : isLoser
                          ? "rgba(220,38,38,0.04)"
                          : "transparent";
                      const deltaColor = isGainer ? "#15803d" : isLoser ? "#b91c1c" : T.textFaint;
                      const arrow = isGainer ? "↑" : isLoser ? "↓" : "—";
                      return (
                        <Box key={row.key} sx={{
                          px: 1.5, py: 0.7,
                          display: "flex", alignItems: "center",
                          bgcolor: rowBg,
                          borderBottom: i < sortedMovement.length - 1 ? `1px solid ${T.border}` : "none",
                        }}>
                          <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: row.color, flexShrink: 0 }} />
                            <Typography sx={{
                              fontSize: "11.5px", fontWeight: 500, color: T.text,
                              fontFamily: "'DM Sans', sans-serif",
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {row.label}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.textMuted, fontFamily: "'DM Sans', sans-serif", minWidth: 44, textAlign: "right", flexShrink: 0 }}>
                            {Number.isFinite(row.endValue) ? `${row.endValue.toFixed(1)}%` : "—"}
                          </Typography>
                          <Box sx={{ minWidth: 54, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3, flexShrink: 0 }}>
                            <Typography sx={{
                              fontSize: "11px", fontWeight: 700, color: deltaColor,
                              fontFamily: "'DM Sans', sans-serif",
                            }}>
                              {arrow} {isGainer ? "+" : ""}{row.delta.toFixed(1)}pp
                            </Typography>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })()}
            </>
          );
        })()}

        {/* Demographic filter banner — amber, above chart */}
        {!compareWaves && hasDemoFilter && (
          <Box sx={{
            mb: 1.5, px: 1.25, py: 0.85,
            borderRadius: "8px",
            bgcolor: "rgba(245,158,11,0.08)",
            border: "1px solid rgba(245,158,11,0.3)",
            display: "flex", alignItems: "flex-start", gap: 0.75,
          }}>
            <TuneIcon sx={{ fontSize: 12, color: "#92400e", mt: "2px", flexShrink: 0 }} />
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
              {Object.entries(selectedDemographics || {}).map(([key, label]) =>
                label ? (
                  <Box key={key} sx={{
                    fontSize: "10px", fontWeight: 600, color: "#92400e",
                    bgcolor: "rgba(245,158,11,0.14)", border: "1px solid rgba(245,158,11,0.3)",
                    borderRadius: "5px", px: 0.75, py: 0.2, lineHeight: 1.6,
                    fontFamily: "'DM Sans', sans-serif",
                  }}>
                    {label}
                  </Box>
                ) : null
              )}
            </Box>
          </Box>
        )}

        {/* Provider comparison panel — above bar chart */}
        {providerFilter === "compare" && (() => {
          const origVar = providerFilterOriginalVar || selectedVariable;
          const providerConfig = groupsById?.[origVar]?.providerFilter;
          if (!providerConfig) return null;

          const getNet = (varKey) => {
            const g = groupsById?.[varKey];
            if (!g?.polarity) return null;
            const pKeys = Array.isArray(g.polarity.pos) ? g.polarity.pos : [g.polarity.pos].filter(Boolean);
            const nKeys = Array.isArray(g.polarity.neg) ? g.polarity.neg : [g.polarity.neg].filter(Boolean);
            const sources = [
              selectedFeatureData?.selected?.[0],
              selectedFeatureData?.constituency?.[0],
              selectedFeatureData?.national?.[0],
            ].filter(Boolean);
            for (const src of sources) {
              const pos = pKeys.reduce((s, k) => s + (Number(src?.[k]) || 0), 0);
              const neg = nKeys.reduce((s, k) => s + (Number(src?.[k]) || 0), 0);
              if (Number.isFinite(pos - neg) && (pos > 0 || neg > 0)) return Math.round(pos - neg);
            }
            return null;
          };

          const compareRows = [
            { label: "All customers", net: getNet(origVar), color: "#64748b" },
            ...providerConfig.providers.map((p) => ({
              label: p.label, net: getNet(p.variable), color: "#0d9488",
            })),
          ];
          const maxAbs = Math.max(1, ...compareRows.map(r => Math.abs(r.net || 0)));

          return (
            <Box sx={{ mx: 2.5, mb: 2, p: 1.5, borderRadius: "10px", border: `1px solid ${T.border}`, bgcolor: "#fafbfc" }}>
              <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: T.textFaint, mb: 1.25, fontFamily: "'DM Sans', sans-serif" }}>
                Net trust by provider
              </Typography>
              {compareRows.every((r, i) => i === 0 || r.net == null) && (
                <Typography sx={{ fontSize: "10px", color: T.textFaint, fontStyle: "italic", mb: 1, fontFamily: "'DM Sans', sans-serif" }}>
                  Click each provider individually to see their data on the map. Full comparison available when provider-modelled data is loaded.
                </Typography>
              )}
              {compareRows.map((r) => {
                const net = r.net;
                const barColor = net != null && net >= 0 ? "#2E7D32" : "#C62828";
                const barW = net != null ? Math.abs(net) / maxAbs * 100 : 0;
                return (
                  <Box key={r.label} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.75 }}>
                    <Typography sx={{ fontSize: "11px", fontWeight: 600, color: T.text, minWidth: 95, flexShrink: 0, fontFamily: "'DM Sans', sans-serif" }}>{r.label}</Typography>
                    <Box sx={{ flex: 1, height: 8, borderRadius: "4px", bgcolor: "rgba(15,23,42,0.06)", position: "relative", overflow: "hidden" }}>
                      <Box sx={{ position: "absolute", left: net >= 0 ? "50%" : `${50 - barW / 2}%`, width: `${barW / 2}%`, height: "100%", bgcolor: barColor, borderRadius: "4px", transition: "all 0.3s ease" }} />
                    </Box>
                    <Typography sx={{ fontSize: "12px", fontWeight: 700, color: barColor, minWidth: 32, textAlign: "right", fontFamily: "'DM Sans', sans-serif" }}>
                      {net != null ? `${net > 0 ? "+" : ""}${net}` : "—"}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          );
        })()}

        {/* Bar chart — hidden when trend mode is active */}
        {!(compareWaves && compareChartMode === "trend") && (() => {
          // Build vs-avg data: diff = (local - baseline) per row.
          // In demo compare mode: group-in-area vs everyone nationally (unfiltered).
          // Otherwise: area vs filtered national average.
          const localKey = isDemoCompareMode ? filteredLocalKey
            : hasSelected ? "selected" : hasConstituency ? "constituency" : null;
          const vsAvgData = vsAvgMode && localKey
            ? sortedData.map((row) => {
                const local = row[localKey];
                const nat = isDemoCompareMode
                  ? (Number.isFinite(row.unfilteredNational) ? row.unfilteredNational : row.national)
                  : row.national;
                const diff = Number.isFinite(local) && Number.isFinite(nat) ? local - nat : null;
                return { ...row, diff };
              })
            : null;
          const activeVsAvg = vsAvgMode && vsAvgData !== null;

          // Domain for diverging chart
          const diffVals = activeVsAvg ? vsAvgData.map((r) => r.diff).filter(Number.isFinite) : [];
          const maxAbs = diffVals.length ? Math.ceil(Math.max(...diffVals.map(Math.abs)) + 2) : 20;
          const vsAvgDomain = [-maxAbs, maxAbs];

          return (
        <ResponsiveContainer width="100%" height={chartHeight}>
        <BarChart
          data={activeVsAvg ? vsAvgData : sortedData}
          layout="vertical"
          margin={{
            top: 8,
            right: 24,
            left:12,
            bottom: 6,
          }}
          barCategoryGap={22}
          barGap={6}
        >
            <XAxis type="number" domain={activeVsAvg ? vsAvgDomain : [minVal, maxVal]} hide />
            <YAxis
              type="category"
              dataKey="label"
              width={yAxisWidth}
              tick={<TickWrap />}
            />
                <CartesianGrid horizontal={false} strokeDasharray="3 3" opacity={0.2} />
                {activeVsAvg && (
                  <ReferenceLine x={0} stroke={T.borderStrong} strokeWidth={1.5} />
                )}

                <RechartsTooltip
                content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;

                  const fmt = (v) =>
                    v == null || isNaN(v)
                      ? "—"
                      : group?.type === "price_elast"
                      ? `$${(v / 100).toFixed(2)}`
                      : `${v.toFixed(1)}%`;
                  const fmtDiff = (v) =>
                    v == null || isNaN(v) ? "—" : `${v >= 0 ? "+" : ""}${v.toFixed(1)}pp`;

                  const entries = activeVsAvg
                    ? [{
                        name: localKey === "selected" ? selectedLabel : constituencyLabel,
                        value: payload.find((p) => p.dataKey === "diff")?.value,
                        isDiff: true,
                        opacity: 1,
                        dotColor: null,
                      }]
                    : isDemoCompareMode
                    ? [
                        {
                          name: `Everyone ${unfilteredLocalKey === "unfilteredNational" ? "nationally" : `in ${selectedconstName || "area"}`}`,
                          value: payload.find((p) => p.dataKey === unfilteredLocalKey)?.value,
                          opacity: 0.4,
                          dotColor: payload[0]?.payload?.color || T.accent,
                        },
                        {
                          name: demoFilterLabel,
                          value: payload.find((p) => p.dataKey === filteredLocalKey)?.value,
                          opacity: 1,
                          dotColor: "#7c3aed",
                        },
                      ]
                    : [
                        hasNational && {
                          name: "National",
                          value: payload.find((p) => p.dataKey === "national")?.value,
                          opacity: 0.35,
                          dotColor: payload[0]?.payload?.color || "#999",
                        },
                        hasConstituency && {
                          name: constituencyLabel,
                          value: payload.find((p) => p.dataKey === "constituency")?.value,
                          opacity: 0.65,
                          dotColor: payload[0]?.payload?.color || "#999",
                        },
                        hasSelected && {
                          name: selectedLabel,
                          value: payload.find((p) => p.dataKey === "selected")?.value,
                          opacity: 1,
                          dotColor: payload[0]?.payload?.color || "#999",
                        },
                      ].filter(Boolean);

                  return (
                    <div
                      style={{
                        background: "#ffffff",
                        border: `1px solid ${T.borderStrong}`,
                        borderRadius: 10,
                        padding: "8px 10px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                        fontSize: 12,
                        lineHeight: 1.4,
                        maxWidth: 240,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      <div style={{ fontWeight: 600, marginBottom: 6, wordWrap: "break-word", whiteSpace: "normal", color: T.text }}>
                        {label}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "12px 1fr auto", columnGap: 6, rowGap: 4, alignItems: "center" }}>
                        {entries.map((e, i) => {
                          const dotColor = e.isDiff
                            ? (e.value >= 0 ? "#16a34a" : "#dc2626")
                            : (e.dotColor || payload[0]?.payload?.color || "#999");
                          return (
                            <React.Fragment key={i}>
                              <div style={{ width: 10, height: 10, borderRadius: 2, backgroundColor: dotColor, opacity: e.opacity }} />
                              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, fontWeight: 500, color: T.textMuted }} title={e.name}>
                                {e.name}
                              </div>
                              <div style={{ textAlign: "right", fontWeight: 600, whiteSpace: "nowrap", color: T.text }}>
                                {e.isDiff ? fmtDiff(e.value) : fmt(e.value)}
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </div>
                    </div>
                  );
                }}
              />

            {activeVsAvg ? (
              <Bar dataKey="diff" barSize={10} radius={[4, 4, 4, 4]}>
                {vsAvgData.map((row, i) => (
                  <Cell key={`d-${i}`} fill={Number.isFinite(row.diff) && row.diff >= 0 ? "#16a34a" : "#dc2626"} fillOpacity={0.85} cursor="pointer" />
                ))}
              </Bar>
            ) : isDemoCompareMode ? (
              <>
                {unfilteredLocalKey && (
                  <Bar dataKey={unfilteredLocalKey} barSize={10} radius={[4, 4, 4, 4]}>
                    {sortedData.map((row, i) => (
                      <Cell key={`uf-${i}`} fill={row.color} fillOpacity={0.3} cursor="pointer" />
                    ))}
                  </Bar>
                )}
                <Bar dataKey={filteredLocalKey} barSize={10} radius={[4, 4, 4, 4]}>
                  {sortedData.map((row, i) => (
                    <Cell key={`fl-${i}`} fill="#7c3aed" fillOpacity={0.85} cursor="pointer" />
                  ))}
                </Bar>
              </>
            ) : (
              <>
                {hasNational && (
                  <Bar dataKey="national" barSize={10} radius={[4, 4, 4, 4]}>
                    {sortedData.map((row, i) => (
                      <Cell key={`n-${i}`} fill={row.color} fillOpacity={0.35} cursor="pointer" />
                    ))}
                  </Bar>
                )}
                {hasConstituency && (
                  <Bar dataKey="constituency" barSize={10} radius={[4, 4, 4, 4]}>
                    {sortedData.map((row, i) => (
                      <Cell key={`c-${i}`} fill={row.color} fillOpacity={0.65} cursor="pointer" />
                    ))}
                  </Bar>
                )}
                {hasSelected && (
                  <Bar dataKey="selected" barSize={10} radius={[4, 4, 4, 4]}>
                    {sortedData.map((row, i) => (
                      <Cell key={`sel-${i}`} fill={row.color} fillOpacity={1} cursor="pointer" />
                    ))}
                  </Bar>
                )}
              </>
            )}
          </BarChart>
        </ResponsiveContainer>
          );
        })()}


        {!compareWaves && (() => {
          // Build vs-avg diff lookup for table (reuse same localKey logic)
          const tblLocalKey = hasSelected ? "selected" : hasConstituency ? "constituency" : null;
          const tblDiffMap = vsAvgMode && tblLocalKey
            ? Object.fromEntries(
                sortedData.map((row) => {
                  const local = row[tblLocalKey];
                  const nat = row.national;
                  const diff = Number.isFinite(local) && Number.isFinite(nat) ? local - nat : null;
                  return [row.label, diff];
                })
              )
            : null;

          return (
          <Box sx={{ mt: 1 }}>
            <Table size="small" sx={{ "& .MuiTableCell-root": { fontFamily: "'DM Sans', sans-serif", borderColor: T.border, fontSize: "12px" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>Label</TableCell>
                  {isDemoCompareMode && !vsAvgMode && (
                    <>
                      <TableCell align="right" sx={{ color: T.textMuted }}>Everyone</TableCell>
                      <TableCell align="right" sx={{ color: "#7c3aed", fontWeight: 600 }}>{demoFilterLabel}</TableCell>
                    </>
                  )}
                  {!isDemoCompareMode && hasNational && !vsAvgMode && <TableCell align="right">National</TableCell>}
                  {!isDemoCompareMode && hasConstituency && !vsAvgMode && (
                    <TableCell align="right">{constituencyLabel}</TableCell>
                  )}
                  {!isDemoCompareMode && hasSelected && !vsAvgMode && (
                    <TableCell align="right">{selectedLabel}</TableCell>
                  )}
                  {vsAvgMode && tblLocalKey && (
                    <TableCell align="right" sx={{ color: "#16a34a", fontWeight: 600 }}>± vs avg</TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {showNRow && !vsAvgMode && (
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: T.textMuted }}>N</TableCell>
                    {hasNational && (
                      <TableCell align="right" sx={{ color: T.textMuted }}>
                        {fmtN(nValues.national)}
                      </TableCell>
                    )}
                    {hasConstituency && (
                      <TableCell align="right" sx={{ color: T.textMuted }}>
                        {fmtN(nValues.constituency)}
                      </TableCell>
                    )}
                    {hasSelected && (
                      <TableCell align="right" sx={{ color: T.textMuted }}>
                        {fmtN(nValues.selected)}
                      </TableCell>
                    )}
                  </TableRow>
                )}
                {sortedData.map((row) => {
                  if (vsAvgMode && tblDiffMap) {
                    const diff = tblDiffMap[row.label];
                    const isFinite = Number.isFinite(diff);
                    const color = !isFinite ? T.textFaint : diff >= 0 ? "#16a34a" : "#dc2626";
                    return (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color }}>
                          {isFinite ? `${diff >= 0 ? "+" : ""}${diff.toFixed(1)}pp` : "—"}
                        </TableCell>
                      </TableRow>
                    );
                  }
                  if (isDemoCompareMode) {
                    const unfVal = unfilteredLocalKey ? row[unfilteredLocalKey] : null;
                    const filtVal = row[filteredLocalKey];
                    const diff = Number.isFinite(filtVal) && Number.isFinite(unfVal) ? filtVal - unfVal : null;
                    const diffColor = diff == null ? "#7c3aed"
                      : diff > 1  ? "#16a34a"
                      : diff < -1 ? "#dc2626"
                      : "#7c3aed";
                    return (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right" sx={{ color: T.textMuted }}>{fmtVal(unfVal)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600, color: diffColor }}>{fmtVal(filtVal)}</TableCell>
                      </TableRow>
                    );
                  }
                  const vals = [
                    hasNational ? row.national : null,
                    hasConstituency ? row.constituency : null,
                    hasSelected ? row.selected : null,
                  ].filter((v) => v != null && Number.isFinite(v));
                  const max = vals.length ? Math.max(...vals) : null;
                  const CellVal = (val) => {
                    const isMax = max != null && val === max;
                    return (
                      <TableCell align="right" sx={{ fontWeight: isMax ? 700 : 400, color: isMax ? T.accent : T.text }}>
                        {fmtVal(val)}
                      </TableCell>
                    );
                  };
                  return (
                    <TableRow key={row.label}>
                      <TableCell>{row.label}</TableCell>
                      {hasNational && CellVal(row.national)}
                      {hasConstituency && CellVal(row.constituency)}
                      {hasSelected && CellVal(row.selected)}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
          );
        })()}
      </AccordionDetails>
    </StyledAccordion>
  );
})()}

<StyledAccordion defaultExpanded>
  <StyledAccordionSummary>
    <SectionLabel>Key Area Statistics</SectionLabel>
  </StyledAccordionSummary>

  <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
    <Box>
      <KeyAreaStats
        extDataBlurb={extDataBlurb}
        selectedAreaId={selectedFeature?.value ?? null}
        extDataBlurbComparison={extDataBlurbComparison}
        selectedFeature={selectedFeature}
        hoverArea={hoverArea}
        selectedFeatureLevel={selectedFeatureLevel}
        selectedconstName={selectedconstName}
      />
    </Box>
  </AccordionDetails>
</StyledAccordion>
  
    
    </Box>
    )}

{/* Area filtering section */}
        {activeTab === "data" && ActionMode === "filter" && (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              "&::-webkit-scrollbar": { width: "4px" },
              "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
              "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
            }}
          >
            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Find Audiences</SectionLabel>
              </StyledAccordionSummary>
              <AreaSearch
                selectedVariable={selectedVariable}
                selected={selected}
                onVariableChange={onVariableChange}
                groupsById={groupsById}
                detailsOpen={detailsOpen}
                ActionTargetVariables={ActionTargetVariables}
                onActionTargetVariableChange={onActionTargetVariableChange}
                ActionTargetSubVariables={ActionTargetSubVariables}
                handleActionTargetSubVariableChange={handleActionTargetSubVariableChange}
                ActionMode={ActionMode}
                ActionTargetConcentration={ActionTargetConcentration}
                handleActionTargetConcentrationChange={handleActionTargetConcentrationChange}
                selectedDemographics={selectedDemographics}
                selectedFeatureData={selectedFeatureData}
              />

              <TopAreasPanel
                items={rankedItems}
                onSelect={(area) => onAreaSelect?.(area)}
                initialCount={10}
                loading={!!mapLoading}
                compareMode={compareWaves}
                showBottomToggle
              />
            </StyledAccordion>

            <StyledAccordion defaultExpanded>
              <StyledAccordionSummary>
                <SectionLabel>Target Demographics</SectionLabel>
              </StyledAccordionSummary>
              <AccordionDetails sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
                <Typography sx={{ fontSize: "12px", color: T.textFaint, mb: 1.75, fontFamily: "'DM Sans', sans-serif" }}>
                  Choose one or more groups to refine your audience.
                </Typography>

                {/* Workflow Box */}
                <Box sx={{
                  mb: 2,
                  p: 1.75,
                  borderRadius: "10px",
                  border: `1px solid ${T.border}`,
                  bgcolor: T.surface,
                }}>
                  <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 1 }}>
                    Targeting Workflow
                  </Typography>

                  <Stack spacing={0.6}>
                    {[
                      ["1", "Response", targetLabel],
                      ["2", "Scope", selectedconstName || "National"],
                      ["3", "See strongest subgroups", null],
                      ["4", "Compute strongest combined audience", null],
                    ].map(([step, label, value]) => (
                      <Stack key={step} direction="row" spacing={1} alignItems="flex-start">
                        <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: T.accentDim, border: `1px solid ${T.chipActiveBorder}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "1px" }}>
                          <Typography sx={{ fontSize: "9px", fontWeight: 700, color: T.accent, fontFamily: "'DM Sans', sans-serif" }}>{step}</Typography>
                        </Box>
                        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                          {label}{value ? <Box component="span" sx={{ fontWeight: 700, color: T.text }}> {value}</Box> : null}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>

                  {audienceInsight?.perDimension && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                        Top Group By Demographic
                      </Typography>
                      {Object.entries(audienceInsight.perDimension).map(([dim, info]) => (
                        <Stack key={dim} direction="row" justifyContent="space-between" sx={{ mb: 0.4 }}>
                          <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                            {dim.charAt(0).toUpperCase() + dim.slice(1)}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: T.text, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                            {info.label} <Box component="span" sx={{ color: T.accent }}>({info.score.toFixed(1)}%)</Box>
                          </Typography>
                        </Stack>
                      ))}
                      {/* Names what the share floor removed, so a missing group
                          reads as excluded rather than as absent data. */}
                      {audienceInsight.minSharePct != null && (
                        <Typography sx={{ fontSize: "10px", color: T.textFaint, mt: 0.75, lineHeight: 1.4, fontFamily: "'DM Sans', sans-serif" }}>
                          {audienceInsight.excludedSmall?.length
                            ? `Groups under ${audienceInsight.minSharePct}% of an area are excluded: ${audienceInsight.excludedSmall.join(", ")}.`
                            : `Groups under ${audienceInsight.minSharePct}% of an area are excluded.`}
                        </Typography>
                      )}
                    </Box>
                  )}

                  {audienceInsight?.perDimension && (() => {
                    const dims = Object.entries(audienceInsight.perDimension);
                    // For the "party" dimension, qualify with "2024 vote" since it
                    // reflects how people voted in the last election, not current intention.
                    const qualifyLabel = (dim, label) => dim === "party" ? `2024 ${label} voters` : label;
                    const personaLine = dims.map(([dim, info]) => qualifyLabel(dim, info.label)).join(", ");
                    const topDim = dims.sort((a, b) => b[1].score - a[1].score)[0];
                    const scope = selectedconstName ? `in ${selectedconstName}` : "nationally";
                    const responseStr = audienceInsight.responseLabel || "this response";
                    const topPair = audienceInsight.topPairs?.[0];
                    const topPairLabel = topPair
                      ? topPair.label.split(" + ").map((part) => {
                          // Re-qualify any party label appearing in combined pairs
                          const matchingDim = dims.find(([, info]) => info.label === part.trim());
                          return matchingDim ? qualifyLabel(matchingDim[0], part.trim()) : part.trim();
                        }).join(" + ")
                      : null;
                    const sentences = [
                      `The most receptive audience for ${responseStr} ${scope} is ${personaLine}.`,
                      topDim ? `${qualifyLabel(topDim[0], topDim[1].label)} show the strongest support at ${topDim[1].score.toFixed(1)}%.` : null,
                      topPairLabel ? `When combining demographics, ${topPairLabel} is the highest-performing segment at ${topPair.score.toFixed(1)}%.` : null,
                      `This audience represents the best starting point for targeted outreach on this issue.`,
                    ].filter(Boolean);
                    return (
                      <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                          Suggested Persona
                        </Typography>
                        <Typography sx={{ fontSize: "11px", fontWeight: 700, color: T.text, fontFamily: "'DM Sans', sans-serif", mb: 0.75, letterSpacing: "0.01em" }}>
                          {dims.map(([dim, info]) => qualifyLabel(dim, info.label)).join(" · ")}
                        </Typography>
                        <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", lineHeight: 1.6 }}>
                          {sentences.join(" ")}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {audienceInsight?.topPairs?.length ? (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", mb: 0.75 }}>
                        Top Combined Audiences
                      </Typography>
                      {audienceInsight.topPairs.map((p, idx) => (
                        <Stack key={`${p.label}-${idx}`} direction="row" spacing={1} alignItems="center" sx={{ mb: 0.4 }}>
                          <Typography sx={{ fontSize: "10px", fontWeight: 700, color: T.textFaint, fontFamily: "'DM Sans', sans-serif", minWidth: 16 }}>{idx + 1}</Typography>
                          <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif", flex: 1 }}>{p.label}</Typography>
                          <Typography sx={{ fontSize: "12px", color: T.accent, fontWeight: 700, fontFamily: "'DM Sans', sans-serif" }}>{p.score.toFixed(1)}%</Typography>
                        </Stack>
                      ))}
                    </Box>
                  ) : audienceInsight?.combined ? (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "12px", color: T.textMuted, fontFamily: "'DM Sans', sans-serif" }}>
                        Strongest combined: <Box component="span" sx={{ color: T.text, fontWeight: 600 }}>{audienceInsight.combined.label}</Box>
                        <Box component="span" sx={{ color: T.accent }}> ({audienceInsight.combined.score.toFixed(1)}%)</Box>
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${T.border}` }}>
                      <Typography sx={{ fontSize: "12px", color: T.textFaint, fontStyle: "italic", fontFamily: "'DM Sans', sans-serif" }}>
                        Use this to identify the most supportive subgroup for outreach.
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Compute Button */}
                <ButtonBase
                  onClick={() => onAudienceInsightRequest?.()}
                  disabled={!!audienceInsightLoading}
                  sx={{
                    width: "100%",
                    py: 1.1,
                    borderRadius: "9px",
                    background: audienceInsightLoading ? T.chip : `linear-gradient(135deg, #3b6fd4, #4f8ef7)`,
                    color: audienceInsightLoading ? T.textFaint : "#fff",
                    fontSize: "13px",
                    fontWeight: 700,
                    fontFamily: "'DM Sans', sans-serif",
                    letterSpacing: "0.01em",
                    boxShadow: audienceInsightLoading ? "none" : "0 4px 16px rgba(37,99,235,0.25)",
                    transition: "all 0.2s ease",
                    "&:hover:not(:disabled)": {
                      background: `linear-gradient(135deg, #2d5bb5, #4080e8)`,
                      boxShadow: "0 6px 20px rgba(37,99,235,0.35)",
                      transform: "translateY(-1px)",
                    },
                    "&:active": { transform: "translateY(0)" },
                    cursor: audienceInsightLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {audienceInsightLoading ? "Computing…" : "Compute Top Audience"}
                </ButtonBase>

                {audienceInsightLoading && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress sx={{
                      height: 3,
                      borderRadius: 999,
                      bgcolor: T.chip,
                      "& .MuiLinearProgress-bar": { bgcolor: T.accent, borderRadius: 999 },
                    }} />
                  </Box>
                )}

                <Stack spacing={1.75} sx={{ mt: 2.5 }}>
                  <Stack direction="row" spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <DemographicGroup icon={PersonIcon} label="Gender" iconColor="#60a5fa">
                        {[[2, "Men"], [1, "Women"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(genderFilter, val)}
                            onClick={() => onGenderChange?.(toggleVal(genderFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                    <Box sx={{ flex: 1.5 }}>
                      <DemographicGroup icon={CakeIcon} label="Age" iconColor="#a78bfa">
                        {[[1, "18–34"], [2, "35–64"], [3, "65+"]].map(([val, lbl]) => (
                          <FilterChip key={val} label={lbl} active={isSel(ageFilter, val)}
                            onClick={() => onAgeChange?.(toggleVal(ageFilter, val))} />
                        ))}
                      </DemographicGroup>
                    </Box>
                  </Stack>

                  <DemographicGroup icon={SchoolIcon} label="Education" iconColor="#fbbf24">
                    {[[1, "Degree"], [2, "No Degree"]].map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(educationFilter, val)}
                        onClick={() => onEducationChange?.(toggleVal(educationFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={GroupsIcon} label="Ethnicity" iconColor="#34d399">
                    {[[1, "White"], [2, "Non White"]].map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(ethnicityFilter, val)}
                        onClick={() => onEthnicityChange?.(toggleVal(ethnicityFilter, val))} />
                    ))}
                  </DemographicGroup>

                  <DemographicGroup icon={HowToVoteIcon} label="Past Vote (2024)" iconColor="#f87171">
                    {pastVoteOptions.map(([val, lbl]) => (
                      <FilterChip key={val} label={lbl} active={isSel(partyFilter, val)}
                        onClick={() => onPartyChange?.(toggleVal(partyFilter, val))} />
                    ))}
                  </DemographicGroup>

                  {segmentAvailable && (
                    <DemographicGroup icon={CategoryIcon} label={segmentGroupLabel} iconColor="#a855f7">
                      {segmentOptions.map(([val, lbl]) => (
                        <FilterChip key={val} label={lbl} active={isSegSel(segmentFilter, val)}
                          onClick={() => onSegmentChange?.(toggleSegVal(segmentFilter, val))} />
                      ))}
                    </DemographicGroup>
                  )}
                </Stack>

                {onClearAll && (
                  <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                    <ButtonBase
                      onClick={onClearAll}
                      sx={{
                        fontSize: "11px",
                        color: T.textFaint,
                        fontFamily: "'DM Sans', sans-serif",
                        fontWeight: 500,
                        letterSpacing: "0.03em",
                        px: 1.25,
                        py: 0.5,
                        borderRadius: "6px",
                        border: `1px solid ${T.border}`,
                        transition: "all 0.15s",
                        "&:hover": { color: T.text, borderColor: T.borderStrong, bgcolor: T.surfaceHover },
                      }}
                    >
                      Clear all
                    </ButtonBase>
                  </Box>
                )}
              </AccordionDetails>
            </StyledAccordion>
          </Box>
        )}

        {/* ── Chat Tab ── */}
        {activeTab === "chat" && (
          <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", bgcolor: T.bg }}>

            {/* Highlight active banner */}
            {chatHighlightCount > 0 && (
              <Box sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                px: 1.75, py: 0.75,
                bgcolor: "rgba(29,78,216,0.06)",
                borderBottom: "1px solid rgba(29,78,216,0.13)",
                flexShrink: 0,
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: "#1d4ed8" }} />
                  <Typography sx={{ fontSize: 11.5, color: "#1d4ed8", fontWeight: 500, fontFamily: "'DM Sans', sans-serif" }}>
                    {chatHighlightCount} area{chatHighlightCount !== 1 ? "s" : ""} highlighted on map
                  </Typography>
                </Box>
                <ButtonBase
                  onClick={onClearHighlights}
                  sx={{
                    fontSize: 11, color: T.textMuted, px: 0.75, py: 0.25, borderRadius: 1,
                    fontFamily: "'DM Sans', sans-serif",
                    "&:hover": { color: T.text },
                  }}
                >
                  Clear ✕
                </ButtonBase>
              </Box>
            )}

            {/* Messages */}
            <Box
              sx={{
                flex: 1,
                overflowY: "auto",
                px: 2,
                py: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 1.5,
                "&::-webkit-scrollbar": { width: "4px" },
                "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
                "&::-webkit-scrollbar-thumb": { bgcolor: T.border, borderRadius: "2px" },
              }}
            >
              {chatMessages.map((msg, i) => {
                const isUser = msg.role === 'user';
                const isErr = msg.isError;
                return (
                  <Box key={i} sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start" }}>
                    <Box
                      sx={{
                        maxWidth: "85%",
                        px: 1.5,
                        py: 1,
                        borderRadius: isUser ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
                        bgcolor: isUser ? T.accent : isErr ? "rgba(220,38,38,0.05)" : T.surface,
                        color: isUser ? "#fff" : isErr ? "#b91c1c" : T.text,
                        fontSize: 13,
                        lineHeight: 1.55,
                        whiteSpace: "pre-wrap",
                        boxShadow: isUser ? "none" : `0 1px 3px rgba(0,0,0,0.06)`,
                        border: isUser ? "none" : isErr ? "1px solid rgba(220,38,38,0.2)" : `1px solid ${T.border}`,
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      {msg.isClarification ? (
                        <>
                          <Box sx={{ mb: msg.clarificationOptions?.length ? 1 : 0 }}>
                            {msg.clarificationQuestion}
                          </Box>
                          {msg.clarificationOptions?.length > 0 && (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.5 }}>
                              {msg.clarificationOptions.map((opt, oi) => (
                                <ButtonBase
                                  key={oi}
                                  onClick={() => onChatSend(opt)}
                                  sx={{
                                    px: 1.25, py: 0.5, borderRadius: "8px",
                                    border: `1px solid ${T.chipActiveBorder}`,
                                    bgcolor: T.chipActive, color: T.accent,
                                    fontSize: 11.5, fontFamily: "'DM Sans', sans-serif",
                                    fontWeight: 500, textAlign: "left",
                                    "&:hover": { bgcolor: "rgba(37,99,235,0.15)" },
                                  }}
                                >
                                  {opt}
                                </ButtonBase>
                              ))}
                            </Box>
                          )}
                        </>
                      ) : msg.content.split(/(\*\*[^*]+\*\*)/g).map((part, j) =>
                        part.startsWith('**') && part.endsWith('**')
                          ? <strong key={j}>{part.slice(2, -2)}</strong>
                          : part
                      )}

                      {/* Sample note caveat */}
                      {!isUser && msg.sampleNote && (
                        <Box sx={{
                          mt: 0.75, px: 1, py: 0.5, borderRadius: 1,
                          bgcolor: "rgba(245,158,11,0.08)",
                          border: "1px solid rgba(245,158,11,0.2)",
                          fontSize: 11, color: "#92400e",
                          fontFamily: "'DM Sans', sans-serif",
                        }}>
                          {msg.sampleNote}
                        </Box>
                      )}

                      {/* Retry button for errors */}
                      {isErr && msg.retryText && (
                        <ButtonBase
                          onClick={() => onChatSend(msg.retryText)}
                          sx={{
                            mt: 0.75, display: "block", fontSize: 11, color: "#b91c1c",
                            textDecoration: "underline", fontFamily: "'DM Sans', sans-serif",
                            "&:hover": { color: "#7f1d1d" },
                          }}
                        >
                          Try again
                        </ButtonBase>
                      )}

                      {/* Source disclosure */}
                      {!isUser && !isErr && msg.queryUsed && (
                        <Box sx={{ mt: 0.5 }}>
                          <Box
                            onClick={() => onChatQueryOpenChange(chatQueryOpen === i ? null : i)}
                            sx={{
                              display: "inline-flex", alignItems: "center", gap: 0.5,
                              cursor: "pointer", color: T.textFaint, fontSize: 11,
                              userSelect: "none", "&:hover": { color: T.textMuted },
                            }}
                          >
                            <InfoOutlinedIcon sx={{ fontSize: 13 }} />
                            {chatQueryOpen === i ? "Hide source" : "What I looked up"}
                          </Box>
                          <Collapse in={chatQueryOpen === i}>
                            <Box sx={{
                              mt: 0.5, p: 1, borderRadius: 1,
                              bgcolor: T.accentGlow, border: `1px solid ${T.border}`,
                              fontSize: 11, color: T.textMuted, fontFamily: "monospace", wordBreak: "break-all",
                            }}>
                              {msg.queryUsed.tool && <div><strong>tool:</strong> {msg.queryUsed.tool}</div>}
                              {msg.queryUsed.metric && <div><strong>metric:</strong> {msg.queryUsed.metric}</div>}
                              {msg.queryUsed.level && <div><strong>level:</strong> {msg.queryUsed.level}</div>}
                              {msg.queryUsed.area_codes?.length > 0 && (
                                <div><strong>areas:</strong> {msg.queryUsed.area_codes.join(', ')}</div>
                              )}
                              {msg.queryUsed.filters && Object.keys(msg.queryUsed.filters).length > 0 && (
                                <div><strong>filters:</strong> {JSON.stringify(msg.queryUsed.filters)}</div>
                              )}
                            </Box>
                          </Collapse>
                        </Box>
                      )}

                      {/* Flag / feedback */}
                      {!isUser && !isErr && msg.logId && (
                        <Box sx={{ mt: 0.75 }}>
                          {flagSubmitted[i] ? (
                            <Typography sx={{ fontSize: 11, color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                              Thanks for the feedback.
                            </Typography>
                          ) : (
                            <>
                              <ButtonBase
                                onClick={() => {
                                  setFlagOpen(flagOpen === i ? null : i);
                                  setFlagCategory("bad_answer");
                                  setFlagNote("");
                                }}
                                sx={{
                                  fontSize: 11, color: flagOpen === i ? "#b91c1c" : T.textFaint,
                                  fontFamily: "'DM Sans', sans-serif", userSelect: "none",
                                  "&:hover": { color: "#b91c1c" },
                                }}
                              >
                                {flagOpen === i ? "Cancel" : "Flag issue"}
                              </ButtonBase>
                              <Collapse in={flagOpen === i}>
                                <Box sx={{
                                  mt: 0.5, p: 1, borderRadius: 1,
                                  bgcolor: "rgba(220,38,38,0.04)", border: "1px solid rgba(220,38,38,0.15)",
                                }}>
                                  <Select
                                    size="small"
                                    value={flagCategory}
                                    onChange={e => setFlagCategory(e.target.value)}
                                    sx={{
                                      fontSize: 11, width: "100%", mb: 0.75,
                                      fontFamily: "'DM Sans', sans-serif",
                                      "& .MuiSelect-select": { py: 0.5 },
                                    }}
                                  >
                                    <MenuItem value="bad_answer" sx={{ fontSize: 11 }}>Answer was inaccurate or unhelpful</MenuItem>
                                    <MenuItem value="wrong_metric" sx={{ fontSize: 11 }}>Wrong metric selected</MenuItem>
                                    <MenuItem value="wrong_tool" sx={{ fontSize: 11 }}>Wrong tool used</MenuItem>
                                    <MenuItem value="no_map" sx={{ fontSize: 11 }}>Map didn't update correctly</MenuItem>
                                    <MenuItem value="other" sx={{ fontSize: 11 }}>Other</MenuItem>
                                  </Select>
                                  <TextField
                                    size="small"
                                    multiline
                                    maxRows={3}
                                    placeholder="Optional note…"
                                    value={flagNote}
                                    onChange={e => setFlagNote(e.target.value)}
                                    fullWidth
                                    sx={{
                                      mb: 0.75,
                                      "& .MuiOutlinedInput-root": {
                                        fontSize: 11, fontFamily: "'DM Sans', sans-serif",
                                        "& fieldset": { borderColor: "rgba(220,38,38,0.2)" },
                                      },
                                    }}
                                  />
                                  <ButtonBase
                                    onClick={async () => {
                                      await onChatFlag(msg.logId, flagCategory, flagNote);
                                      setFlagSubmitted(prev => ({ ...prev, [i]: true }));
                                      setFlagOpen(null);
                                    }}
                                    sx={{
                                      px: 1.25, py: 0.5, borderRadius: 1,
                                      bgcolor: "rgba(220,38,38,0.08)", color: "#b91c1c",
                                      border: "1px solid rgba(220,38,38,0.25)",
                                      fontSize: 11, fontFamily: "'DM Sans', sans-serif",
                                      fontWeight: 600,
                                      "&:hover": { bgcolor: "rgba(220,38,38,0.14)" },
                                    }}
                                  >
                                    Submit flag
                                  </ButtonBase>
                                </Box>
                              </Collapse>
                            </>
                          )}
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
              {chatLoading && (
                <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={16} sx={{ color: T.accent }} />
                  <Typography sx={{ fontSize: 11.5, color: T.textFaint, fontFamily: "'DM Sans', sans-serif" }}>
                    Looking up data…
                  </Typography>
                </Box>
              )}
              <div ref={chatBottomRef} />
            </Box>

            {/* Input */}
            <Box sx={{ px: 1.5, pb: 2, pt: 1, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
              <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                <TextField
                  multiline
                  maxRows={4}
                  value={chatInput}
                  onChange={e => onChatInputChange(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      onChatSend();
                    }
                  }}
                  placeholder="Ask about the data…"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={chatLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: T.surface, color: T.text, fontSize: 13,
                      borderRadius: 2, fontFamily: "'DM Sans', sans-serif",
                      "& fieldset": { borderColor: T.border },
                      "&:hover fieldset": { borderColor: T.borderStrong },
                      "&.Mui-focused fieldset": { borderColor: T.accent },
                    },
                    "& .MuiInputBase-input::placeholder": { color: T.textFaint, opacity: 1 },
                  }}
                />
                <IconButton
                  onClick={() => onChatSend()}
                  disabled={!chatInput.trim() || chatLoading}
                  sx={{ color: chatInput.trim() && !chatLoading ? T.accent : T.textFaint, p: 1 }}
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </Box>
              <Typography sx={{ fontSize: 10, color: T.textFaint, mt: 0.75, textAlign: "center", fontFamily: "'DM Sans', sans-serif" }}>
                Survey data only · Not a prediction tool
              </Typography>
            </Box>
          </Box>
        )}

</Box>



  <Tooltip title={isPanelOpen ? "Collapse panel" : "Expand panel"} placement="right" arrow>
    <Box
      role="button"
      tabIndex={0}
      aria-label={isPanelOpen ? "Hide panel" : "Show panel"}
      onClick={togglePanel}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && togglePanel()}
      sx={{
        position: "relative",
        height: "100%",
        width: "28px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: T.surface,
        borderLeft: `1px solid ${T.border}`,
        boxShadow: "4px 0 12px rgba(0,0,0,0.06)",
        cursor: "pointer",
        zIndex: 2000,
        transition: "background 0.15s",
        "&:hover": { bgcolor: T.surfaceHover },
      }}
    >
      <Box sx={{
        width: 20,
        height: 20,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: T.chip,
        border: `1px solid ${T.borderStrong}`,
      }}>
        {isPanelOpen
          ? <ChevronRightIcon sx={{ fontSize: 14, color: T.textFaint }} />
          : <ChevronLeftIcon sx={{ fontSize: 14, color: T.textFaint }} />
        }
      </Box>
    </Box>
  </Tooltip>
</Box>


  );
};

export default Sidebar;
